/**
 * Practice area content registry. Each entry drives PracticeAreaTemplate.
 * Phase 2A ships only Probate; Phase 2B will add the remaining 11.
 */
import { extraPracticeAreas, extraPracticeAreaLabels } from './practiceAreasExtra';

export interface FaqEntry {
  question: string;
  answer: string;
}

export interface SubService {
  title: string;
  description: string;
  bullets?: string[];
}

export interface PracticeArea {
  slug: string; // URL slug, e.g. 'probate'
  url: string; // full URL path with trailing slash, e.g. '/probate/'
  title: string; // <title> tag
  metaDescription: string; // <meta name="description">
  heroEyebrow: string; // small uppercase label above H1
  heroHeadline: string; // H1
  heroHeadlineAccent?: string; // word(s) in gold italic at end of headline
  heroLead: string; // first paragraph after H1 (40-60 words)
  overview: {
    headline: string;
    paragraphs: string[]; // 2-4 paragraphs
  };
  subServices: SubService[]; // 4-7 entries
  nyFacts: {
    headline: string;
    intro: string;
    facts: Array<{ label: string; value: string }>; // 4-8 labeled facts
  };
  process: {
    headline: string;
    intro: string;
    steps: Array<{ title: string; description: string }>; // 3-6 steps
  };
  faq: FaqEntry[]; // 8-12 entries
  cta: {
    headline: string;
    body: string;
  };
  related: string[]; // slugs of related practice areas (3-5)
  author: {
    name: string;
    title: string;
    credentials: string; // brief credentials string
  };
}

export const practiceAreas: Record<string, PracticeArea> = {
  probate: {
    slug: 'probate',
    url: '/probate/',
    title: "NYC Probate Attorney | New York Surrogate's Court | Morgan Legal",
    metaDescription:
      "Experienced New York City probate attorneys guide executors through Surrogate's Court. EPTL & SCPA compliant. Russel Morgan, Esq. Free consultation: (888) 529-1315.",
    heroEyebrow: "Probate · Surrogate's Court",
    heroHeadline: 'New York Probate',
    heroHeadlineAccent: 'Done Right',
    heroLead:
      "Morgan Legal Group guides executors, administrators, and beneficiaries through every stage of New York probate. From filing the petition in Surrogate's Court to final distribution, we minimize delay, protect the estate, and resolve disputes before they spiral.",
    overview: {
      headline: 'What is Probate in New York?',
      paragraphs: [
        "Probate is the court-supervised process of proving the validity of a will, paying the decedent's debts and taxes, and distributing the remaining assets to the rightful beneficiaries. In New York, probate is administered by the Surrogate's Court of the county in which the decedent resided at death. The governing statutes are the Estates, Powers and Trusts Law (EPTL) and the Surrogate's Court Procedure Act (SCPA).",
        'A New York estate that includes real property, financial accounts not held in trust or joint name, or business interests typically must pass through probate. The named executor (or, if there is no will, a court-appointed administrator) is responsible for marshaling the assets, providing legally required notice to heirs and creditors, filing accountings with the court, and ultimately securing the decree that authorizes distribution.',
        'Probate in New York commonly takes seven to twelve months for an uncontested estate; contested matters or estates with complex assets can take two to three years. The court will not accept the petition without a properly drafted citation, an executed Will, an original death certificate, and (where required) an affidavit of attesting witness or due execution affidavit.',
      ],
    },
    subServices: [
      {
        title: 'Probate Petition & Court Filing',
        description:
          "We prepare and file the SCPA § 1402 petition, citation, will, death certificate, oath and designation, and all related affidavits in the proper Surrogate's Court. Letters Testamentary are typically issued within four to eight weeks of a complete filing.",
        bullets: [
          'SCPA § 1402 verified petition',
          'Citation issuance and service',
          'Will deposit and proof',
          'Letters Testamentary issuance',
        ],
      },
      {
        title: 'Asset Marshaling & Inventory',
        description:
          'Once Letters issue, we help the executor locate, secure, value, and inventory all probate assets. We coordinate with appraisers, banks, brokerage firms, and the New York State Department of Taxation and Finance.',
        bullets: [
          'Bank and brokerage account collection',
          'Real estate appraisal coordination',
          'Business interest valuation',
          'Inventory filing (NYCT-99)',
        ],
      },
      {
        title: 'Creditor Notice & Debt Resolution',
        description:
          'We publish the seven-month creditor notice under SCPA § 1801, evaluate every claim against the estate, negotiate settlements where appropriate, and reject improper claims with formal notice.',
        bullets: [
          'Seven-month creditor publication',
          'Claim review and negotiation',
          'Medicaid lien resolution',
          'Final tax clearance',
        ],
      },
      {
        title: 'Tax Returns & Estate Tax',
        description:
          "We prepare the federal Form 706 (when required), the New York State ET-706 estate tax return, and the decedent's final 1040. We work with the executor's CPA to ensure timely filing and maximize available deductions.",
        bullets: [
          'Federal Form 706',
          'NY ET-706 (estates over roughly $7.35M)',
          'Final personal 1040',
          'Fiduciary income tax 1041',
        ],
      },
      {
        title: 'Will Contests & Litigation',
        description:
          "If a will contest is filed under SCPA § 1404 or after probate via objections, we represent proponents or contestants through SCPA 1404 examinations, motion practice, depositions, and trial in Surrogate's Court.",
        bullets: [
          'SCPA 1404 pre-objection examinations',
          'Lack of capacity defense or challenge',
          'Undue influence litigation',
          'Forensic document review',
        ],
      },
      {
        title: 'Accounting & Final Distribution',
        description:
          'We prepare the informal or judicial accounting (SCPA § 2208 / § 2209), obtain receipts and releases from beneficiaries, and shepherd the executor to discharge so personal liability ends with court approval.',
        bullets: [
          'Informal accounting and R&R',
          'Judicial accounting petitions',
          'Executor commission calculation',
          'Decree of judicial settlement',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Probate — Key Facts',
      intro: 'The numbers and timelines that govern every New York estate.',
      facts: [
        { label: 'Governing statutes', value: 'NY EPTL (substantive) + SCPA (procedural)' },
        { label: 'Court', value: "Surrogate's Court of decedent's county of residence" },
        { label: 'Typical duration (uncontested)', value: '7–12 months' },
        { label: 'Typical duration (contested)', value: '18–36 months' },
        { label: 'Creditor notice period', value: '7 months (SCPA § 1801)' },
        { label: 'NY estate tax exemption (2026)', value: 'approx. $7.35 million' },
        { label: 'NY estate tax "cliff"', value: '105% of exemption → entire estate taxed' },
        { label: 'Executor commission (under $100k)', value: '5% on first $100,000' },
      ],
    },
    process: {
      headline: 'Our Probate Process',
      intro: 'A defined path from your first call to final distribution.',
      steps: [
        {
          title: 'Free 30-Minute Consultation',
          description:
            'We review the will, death certificate, and a quick asset inventory to scope the case and answer your most urgent questions — no obligation.',
        },
        {
          title: 'Engagement & Fee Letter',
          description:
            "Transparent flat-fee or hourly engagement with milestone billing. You know what you're paying before we touch a single filing.",
        },
        {
          title: 'Petition Filed Within 14 Days',
          description:
            'We prepare and file the SCPA § 1402 petition, citation, and supporting affidavits within two weeks of engagement.',
        },
        {
          title: 'Letters Testamentary',
          description:
            'You receive court-issued Letters that authorize you to act for the estate. Banks and brokerages will recognize them immediately.',
        },
        {
          title: 'Asset Marshaling & Notice',
          description:
            'We work in parallel: gathering assets and serving the seven-month creditor notice so the clock starts running early.',
        },
        {
          title: 'Accounting & Distribution',
          description:
            'After taxes and creditors are cleared, we prepare the accounting, obtain releases, and distribute the residue.',
        },
      ],
    },
    faq: [
      {
        question: 'How long does probate take in New York?',
        answer:
          'A straightforward, uncontested New York probate generally takes 7 to 12 months from filing to final distribution. The seven-month creditor notice under SCPA § 1801 is the single longest fixed delay. Contested matters routinely exceed two years.',
      },
      {
        question: 'Do I need a probate attorney in New York?',
        answer:
          "The Surrogate's Court will accept a pro se petition, but the procedural complexity (citation service, due execution proof, accounting requirements) and the executor's personal liability for mistakes make attorney representation strongly advisable for any estate above the small-estate threshold.",
      },
      {
        question: 'What is the small-estate procedure in New York?',
        answer:
          'Under SCPA Article 13, an estate with personal property valued at $50,000 or less (exclusive of real estate) may qualify for the simplified Voluntary Administration procedure. The petition is shorter, the fee is lower, and no formal accounting is typically required.',
      },
      {
        question: 'How much does probate cost in New York?',
        answer:
          'Court filing fees scale with estate size, ranging from $45 (under $10,000) to $1,250 (over $500,000). Attorney fees vary by complexity; Morgan Legal Group offers flat-fee engagements for uncontested estates and transparent hourly billing for contested matters. Executor commissions are set by SCPA § 2307.',
      },
      {
        question: 'Can I avoid probate in New York?',
        answer:
          'Yes. Assets held in a properly funded revocable living trust, jointly with right of survivorship, with named beneficiaries (life insurance, IRAs, 401(k)s, TOD/POD accounts), or held as tenants by the entirety pass outside of probate. Comprehensive estate planning can move most assets out of the probate path.',
      },
      {
        question: 'What happens if there is no will?',
        answer:
          'When a New York resident dies intestate (without a will), the estate passes under EPTL § 4-1.1 to surviving spouse, descendants, parents, or siblings in a defined statutory order. A court-appointed administrator (rather than an executor) handles the estate, generally under more restrictive court supervision.',
      },
      {
        question: 'What if a beneficiary contests the will?',
        answer:
          'Contests are filed as formal objections after SCPA 1404 examinations. The most common grounds are lack of testamentary capacity, undue influence, fraud, and improper execution. We defend or prosecute objections through deposition, motion practice, and trial as needed.',
      },
      {
        question: 'Where is probate filed in New York?',
        answer:
          "In the Surrogate's Court of the county where the decedent was domiciled at death. New York City has separate Surrogate's Courts in Manhattan (60 Centre Street), Brooklyn (2 Johnson Street), Queens, the Bronx, and Staten Island. Long Island estates file in Nassau or Suffolk.",
      },
      {
        question: 'Is real estate part of probate?',
        answer:
          "New York real estate owned solely by the decedent is a probate asset, but title vests in the heirs immediately at death subject to administration. A real estate sale during probate requires the executor's authority (Letters Testamentary) and, for non-resident decedents, ancillary probate.",
      },
      {
        question: 'How are executors compensated?',
        answer:
          "SCPA § 2307 sets executor commissions on a sliding scale: 5% on the first $100,000, 4% on the next $200,000, 3% on the next $700,000, 2.5% on the next $4,000,000, and 2% above $5,000,000 of estate value passing through the executor's hands.",
      },
    ],
    cta: {
      headline: 'Need Probate Counsel in New York?',
      body: "Schedule a free 30-minute consultation with Russel Morgan, Esq. We'll review the will, scope the case, and quote a transparent fee — no obligation.",
    },
    related: ['estate-planning', 'trusts', 'elder-law', 'guardianship'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
  'estate-planning': {
    slug: 'estate-planning',
    url: '/estate-planning/',
    title: 'Estate Planning Attorney NYC | Wills, Trusts, POA | Morgan Legal',
    metaDescription:
      'New York estate planning — wills, revocable and irrevocable trusts, powers of attorney, healthcare directives. Free consultation: (888) 529-1315.',
    heroEyebrow: 'Estate Planning · NYC & NY State',
    heroHeadline: 'Comprehensive Estate Planning for',
    heroHeadlineAccent: 'New York Families',
    heroLead:
      'Morgan Legal Group builds estate plans that protect your legacy, minimize New York and federal estate tax, and keep your family out of court. From a first will at 30 to a multi-generational trust at 70, every plan is drafted around your assets, your beneficiaries, and the specific rules of New York law.',
    overview: {
      headline: 'What Goes Into a New York Estate Plan?',
      paragraphs: [
        'A modern New York estate plan is not a single document — it is a coordinated set of instruments that work together: a Last Will & Testament, one or more trusts, a NY statutory short-form Power of Attorney, a Healthcare Proxy, a Living Will, and properly coordinated beneficiary designations on every retirement, life insurance, and transfer-on-death account. Each instrument controls a different category of asset, and the plan only works if all six layers point in the same direction.',
        "The choice between a will-only plan and a trust-based plan turns on three questions: do you own real property, what is the projected value of your estate at death, and how much privacy and probate avoidance do you want? A will-only plan is simpler and cheaper to draft but leaves every probate-eligible asset to pass through the Surrogate's Court. A funded revocable living trust skips probate entirely, keeps your asset list out of the public record, and lets a successor trustee step in immediately at incapacity or death.",
        'New York imposes its own estate tax separately from the federal estate tax. The New York exemption is approximately $7.35 million per decedent, and the tax is structured as a notorious "cliff" — if the taxable estate exceeds 105% of the exemption (roughly $7.72 million), the exemption is lost entirely and the full estate is taxed from dollar one. The federal exemption has been cut roughly in half by the 2026 sunset, with rates climbing to 40%. Sophisticated New York planning frequently targets the gap between these two thresholds.',
        'A good plan is also revisited. Marriages, divorces, births, deaths, moves, large liquidity events, business sales, and major statutory changes (the 2021 NY POA reform, the federal SECURE Act, anticipated 2026 sunset of the elevated federal exemption) all reopen decisions made years earlier. We recommend a substantive review every three to five years and after every major life event.',
      ],
    },
    subServices: [
      {
        title: 'Last Will & Testament',
        description:
          'The foundation document. We draft wills that name guardians for minor children, appoint executors and successor executors, exercise powers of appointment, fund testamentary trusts, and meet the strict execution formalities of EPTL § 3-2.1.',
        bullets: [
          'EPTL § 3-2.1 two-witness execution',
          'Self-proving affidavit at signing',
          'Codicils and amendments',
          'Holographic wills (active military only in NY)',
          'Pour-over wills paired with revocable trust',
        ],
      },
      {
        title: 'Revocable Living Trust',
        description:
          'A revocable trust holds title to your assets during your lifetime, lets you serve as your own trustee, and passes the assets to your named beneficiaries at death without probate. Funding the trust — actually retitling your real estate, brokerage accounts, and business interests into the trust — is where most DIY plans fail.',
        bullets: [
          'Grantor-trustee revocable structure',
          'Real estate retitling and deed preparation',
          'Brokerage and bank account funding',
          'Certificate of trust for third parties',
          'Pour-over will as backstop for unfunded assets',
        ],
      },
      {
        title: 'Irrevocable Trusts',
        description:
          'When the goal is to remove assets from your taxable estate, protect them from creditors, or qualify for Medicaid after the five-year lookback, an irrevocable trust is the right tool. We draft Medicaid Asset Protection Trusts, Irrevocable Life Insurance Trusts, dynasty trusts that take advantage of the generation-skipping transfer (GST) tax exemption, and grantor-trust variations that shift income to the lower-tax beneficiary.',
        bullets: [
          'Medicaid Asset Protection Trust (MAPT)',
          'Irrevocable Life Insurance Trust (ILIT)',
          'Dynasty trust with GST allocation',
          'Spousal Lifetime Access Trust (SLAT)',
          'Intentionally Defective Grantor Trust (IDGT)',
        ],
      },
      {
        title: 'Powers of Attorney',
        description:
          'New York completely overhauled its POA statute effective June 13, 2021. Every POA executed after that date must use the new statutory short form under GOL Article 5 Title 15. Gifts above $5,000 per year require a separate signed Statutory Gifts Rider. Old long-form POAs are still valid if properly executed but cannot be amended; new circumstances require a new form.',
        bullets: [
          'NY statutory short form (post 6/13/2021)',
          'Monetary gift rider for gifts > $5,000/yr',
          'Springing vs. immediately effective',
          'Successor agent designation',
          'Coordinating with HIPAA release',
        ],
      },
      {
        title: 'Healthcare Proxy & Living Will',
        description:
          'A Healthcare Proxy under PHL § 2980 names an agent to make medical decisions when you cannot speak for yourself. A Living Will memorializes your specific wishes on life-sustaining treatment. For patients with serious advanced illness, a MOLST (Medical Orders for Life-Sustaining Treatment) form, signed by a physician, gives EMS and hospital staff actionable orders that the Proxy alone cannot.',
        bullets: [
          'NY Healthcare Proxy (PHL § 2980)',
          'Living Will with specific directives',
          'MOLST for advanced illness',
          'HIPAA authorization',
          'Out-of-state portability review',
        ],
      },
      {
        title: 'Beneficiary Designation Coordination',
        description:
          'Retirement accounts, life insurance, and TOD/POD accounts pass by contract — not by your will. A perfect will is undone the moment a stale beneficiary designation names an ex-spouse. We audit every account, align designations with the plan, and document the SECURE Act 10-year payout rules for non-spouse beneficiaries.',
        bullets: [
          '401(k), IRA, and 403(b) beneficiary review',
          'Life insurance primary and contingent',
          'TOD / POD account titling',
          'SECURE Act 10-year payout planning',
          'Per stirpes vs. per capita election',
        ],
      },
      {
        title: 'Lifetime Gifting Strategy',
        description:
          'New York imposes no gift tax, which makes lifetime giving one of the few levers that genuinely moves a New York taxable estate. Two rules decide whether it works. Timing: Tax Law § 954(a)(3) pulls gifts made within three years of death back into the New York gross estate, so deathbed giving buys nothing. Basis: property given away carries your cost basis to the recipient, while the same property held until death takes a stepped-up basis under IRC § 1014. Appreciated assets are usually the wrong thing to give and the right thing to inherit — cash and high-basis assets go first.',
        bullets: [
          'No New York gift tax — only the federal annual exclusion applies',
          'Three-year add-back under NY Tax Law § 954(a)(3)',
          'Carryover basis on gifts vs. § 1014 step-up at death',
          '529 plan five-year front-loading election',
          'Gifts over $5,000/yr require a POA Monetary Gifts Rider',
        ],
      },
      {
        title: 'Charitable Planning',
        description:
          'A charitable bequest comes straight off the New York taxable estate, and here that arithmetic is unusually sharp: an estate sitting just above the cliff can drop back under the exemption through a gift that would otherwise have left as tax. Lifetime structures do more than a bequest. A Charitable Remainder Trust pays you or your family for a term of years and leaves what remains to charity; a Charitable Lead Trust reverses the order, sending income to charity first and the remainder to your beneficiaries. For an IRA owner past 70½, a qualified charitable distribution satisfies part of the required minimum distribution without adding a dollar to taxable income.',
        bullets: [
          'Charitable Remainder Trust (CRT) — income first, charity after',
          'Charitable Lead Trust (CLT) — charity first, family after',
          'Bequest sized to clear the New York estate tax cliff',
          'Qualified Charitable Distribution from an IRA at 70½+',
          'Donor-advised fund — deduction now, granting later',
        ],
      },
      {
        title: 'Choosing a Trustee and Executor',
        description:
          'The instrument is only as good as the person who administers it. A New York trustee is held to the prudent investor standard of EPTL § 11-2.3 and owes an undivided duty of loyalty to every beneficiary — which is exactly where a well-meaning family appointment goes wrong, because the child who serves as trustee is usually also a beneficiary. Commissions are set by statute (SCPA § 2309 for trustees, § 2307 for executors), so an unpaid family member is not the saving it appears to be once litigation starts. We match the appointment to the assets: an individual for a straightforward plan, a corporate or professional trustee where the trust runs for decades, holds a business, or serves a beneficiary who cannot manage funds.',
        bullets: [
          'Prudent investor standard — EPTL § 11-2.3',
          'Statutory commissions — SCPA § 2309 and § 2307',
          'Conflicts when the trustee is also a beneficiary',
          'Individual vs. corporate or professional trustee',
          'Named successors, and how a trustee is removed',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Estate Planning — Key Facts',
      intro: 'Statutes, thresholds, and timelines that shape every New York plan.',
      facts: [
        { label: 'Will execution', value: 'EPTL § 3-2.1 — 2 witnesses, signed within 30 days' },
        { label: 'Power of Attorney form', value: 'NY statutory short form, effective 6/13/2021' },
        { label: 'POA gift threshold', value: '$5,000/yr without a Monetary Gifts Rider' },
        { label: 'NY estate tax exemption (2026)', value: 'approx. $7.35 million' },
        { label: 'NY estate tax "cliff"', value: '>105% of exemption → entire estate taxed' },
        { label: 'Federal estate tax exemption', value: 'halved by the 2026 sunset' },
        { label: 'Healthcare Proxy authority', value: 'NY PHL § 2980' },
        { label: 'Spousal elective share', value: 'EPTL § 5-1.1-A — greater of $50K or 1/3' },
        { label: 'New York gift tax', value: 'none — but see the three-year add-back' },
        { label: 'Gift add-back window', value: 'Tax Law § 954(a)(3) — 3 years before death' },
        { label: 'Trustee standard of care', value: 'prudent investor rule, EPTL § 11-2.3' },
        { label: 'Trustee commissions', value: 'SCPA § 2309 (executors: § 2307)' },
      ],
    },
    process: {
      headline: 'Our Estate Planning Process',
      intro: 'A defined path from first call to a fully executed, fully funded plan.',
      steps: [
        {
          title: 'Initial Consultation',
          description:
            'A free 30-minute call to understand your family, your assets, and your concerns. No forms to fill out beforehand — bring questions.',
        },
        {
          title: 'Goals & Inventory',
          description:
            'We build a complete picture: real estate, retirement accounts, life insurance, business interests, digital assets, and family dynamics. From this, we recommend a will-only, trust-based, or hybrid plan.',
        },
        {
          title: 'Document Drafting',
          description:
            'Drafts are delivered within 14 to 21 days of engagement and walked through paragraph by paragraph in a review meeting. Revisions are included.',
        },
        {
          title: 'Execution Ceremony',
          description:
            'We host the signing at our Manhattan office. Two qualified witnesses, a notary, and a self-proving affidavit are arranged so the will is admissible to probate without further proof.',
        },
        {
          title: 'Funding & Coordination',
          description:
            'For trust-based plans we prepare deeds, brokerage retitling letters, and beneficiary designation updates. Funding is where most plans break — we make sure yours does not.',
        },
        {
          title: 'Periodic Review',
          description:
            'A complimentary review every three years and after every major life event. Laws change; your plan should change with them.',
        },
      ],
    },
    faq: [
      {
        question: 'Do I need a will if I already have a trust?',
        answer:
          'Yes. Even with a fully funded revocable trust, you should have a "pour-over" will that catches any asset that was not retitled into the trust before death. The pour-over will directs those stray assets into the trust for unified distribution. It also names a guardian for minor children — something a trust cannot do.',
      },
      {
        question: 'What is a pour-over will?',
        answer:
          'A pour-over will is a short will used alongside a revocable living trust. Instead of distributing assets directly to named beneficiaries, it "pours" any assets the testator owned at death into the existing trust, which then governs distribution. It is the safety net for a trust-based plan.',
      },
      {
        question: 'How often should I update my estate plan?',
        answer:
          'A substantive review every three to five years and after every major life event: marriage, divorce, birth or adoption, death of a named beneficiary or fiduciary, move to or from New York, business sale, large inheritance, or a major change in the tax law. The 2021 NY POA reform alone made millions of older POAs unusable for new transactions.',
      },
      {
        question: 'Are online will templates safe for New York?',
        answer:
          'Generic templates frequently fail to satisfy New York\'s execution rules under EPTL § 3-2.1, do not address the spousal elective share, do not coordinate with the 2021 NY POA statutory form, and ignore the NY estate tax cliff. A will that does not meet the witnessing formalities is invalid and the estate passes by intestacy.',
      },
      {
        question: 'Will versus revocable trust — which is right for me?',
        answer:
          'A will-only plan is appropriate for younger clients with simple assets and no real property. A revocable trust makes sense when you own real estate (especially in multiple states, where it avoids ancillary probate), value privacy, want a clean incapacity backstop, or want your beneficiaries to receive their inheritance without months of court delay.',
      },
      {
        question: 'What is the difference between the federal and NY estate tax thresholds?',
        answer:
          'The New York exemption is approximately $7.35 million, while the federal exemption has been roughly halved by the 2026 sunset. New York has no portability between spouses, while federal does. The unique New York "cliff" eliminates the exemption entirely when the estate exceeds 105% of the threshold, creating tax planning opportunities specifically targeted at this gap.',
      },
      {
        question: 'Can I disinherit my spouse in New York?',
        answer:
          'No. Under EPTL § 5-1.1-A, a surviving spouse has an "elective share" equal to the greater of $50,000 or one-third of the net estate (including most non-probate assets). A will or trust that attempts to leave the spouse less than the elective share is enforceable as written only if the spouse fails to elect within six months of letters issuing — and the spouse usually does elect.',
      },
      {
        question: 'What is a MAPT and when should I consider one?',
        answer:
          'A Medicaid Asset Protection Trust is an irrevocable trust that holds assets long enough to survive Medicaid\'s five-year lookback under SSL § 366. Assets placed in a properly drafted MAPT five or more years before a Medicaid application are not counted toward the eligibility limit and are not subject to estate recovery. MAPTs are typically considered between ages 60 and 70.',
      },
      {
        question: 'Do digital assets need separate planning?',
        answer:
          'Yes. Cryptocurrency, online accounts, domain names, cloud storage, and social media require explicit authorization in the POA and will under the New York Revised Uniform Fiduciary Access to Digital Assets Act (EPTL Article 13-A). Without this authorization, custodians may legally refuse to provide access to a fiduciary, even with letters testamentary.',
      },
      {
        question: 'Does New York have a gift tax?',
        answer:
          'No. New York repealed its gift tax, so lifetime giving is limited only by the federal rules. The catch is Tax Law § 954(a)(3): a gift made within three years of death is added back into the New York gross estate as if it never happened. Gifting is a planning tool for people with time, not a deathbed maneuver — and because gifts carry your cost basis while inherited property is stepped up under IRC § 1014, the asset you choose to give matters as much as when you give it.',
      },
      {
        question: 'Should I put my house in my children\u2019s names now?',
        answer:
          'Almost never as an outright transfer. You lose the stepped-up basis, so the same house your children could have inherited and sold with little or no capital gain becomes a taxable gain measured from what you paid decades ago. You expose the house to your child\u2019s divorce, creditors, and judgments. You may lose STAR and senior exemptions, and the transfer still starts the Medicaid five-year lookback. A Medicaid Asset Protection Trust reaches the same protection while keeping the basis step-up and your right to live there.',
      },
      {
        question: 'Who should serve as my trustee?',
        answer:
          'Someone who will still be capable in twenty years, is willing to be unpopular, and has no stake in the outcome. New York holds trustees to the prudent investor standard of EPTL § 11-2.3 and pays them statutory commissions under SCPA § 2309, so a family member serving "for free" is neither free nor unaccountable. Where the trust will run for decades, hold a business, or make discretionary distributions to a beneficiary who cannot manage money, a corporate or professional trustee — often paired with a family member as co-trustee — avoids putting one child in the position of judging another.',
      },
      {
        question: 'How much does a comprehensive estate plan cost?',
        answer:
          'Flat fees vary by complexity. A will-based plan for a single individual with simple assets is typically less than a trust-based plan for a married couple with real estate, business interests, and an ILIT. We quote the full fee in writing after the initial consultation — there are no hourly surprises and no "per page" charges.',
      },
    ],
    cta: {
      headline: 'Start Your New York Estate Plan',
      body: 'Schedule a free 30-minute consultation with Russel Morgan, Esq. No pressure, no boilerplate — a real conversation about your family, your assets, and the plan that fits.',
    },
    related: ['probate', 'trusts', 'asset-protection', 'elder-law'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
  trusts: {
    slug: 'trusts',
    url: '/trusts/',
    title: 'Wills & Trusts Attorney NYC | Revocable & Irrevocable | Morgan Legal',
    metaDescription:
      'Drafted properly under EPTL § 3-2.1 and Article 7. Revocable trusts, irrevocable trusts, Medicaid asset-protection trusts. Free consultation (888) 529-1315.',
    heroEyebrow: 'Wills & Trusts · EPTL Article 3 & 7',
    heroHeadline: 'Wills & Trusts',
    heroHeadlineAccent: 'Built to Hold Up',
    heroLead:
      'A will or trust is only as strong as the drafting and execution behind it. Morgan Legal Group prepares instruments that survive the scrutiny of the Surrogate\'s Court, the New York State Department of Taxation, and any disappointed beneficiary. Precise statutory language, witnessed signings, and disciplined funding — every time.',
    overview: {
      headline: 'Wills vs. Trusts in New York',
      paragraphs: [
        "A will is a posthumous instrument: it has no legal effect during your lifetime and takes hold only after it is admitted to probate by the Surrogate's Court. A trust, by contrast, is a living legal entity. Once funded, it owns property in its own name and operates under the rules you set down in the trust agreement. Both can serve as the primary vehicle for distributing your estate — the right choice depends on what you own, where you own it, and how much court involvement you are willing to tolerate.",
        'New York execution formalities are unforgiving. Under EPTL § 3-2.1, a will must be signed at the end by the testator, the testator must declare to two witnesses that the instrument is their will, the witnesses must sign within 30 days of each other, and the testator must be at least 18 and of sound mind. Failure on any prong invalidates the will, and the estate passes by intestacy under EPTL § 4-1.1. We invariably attach a self-proving affidavit so the witnesses are not required to appear at probate.',
        'A revocable trust avoids probate entirely for any asset titled in the name of the trust. It does not, however, save New York or federal estate tax — for income and estate-tax purposes, a revocable trust is transparent. Tax savings come from irrevocable trusts: a Medicaid Asset Protection Trust to qualify for long-term care, an Irrevocable Life Insurance Trust to keep death benefit out of the taxable estate, or a Special Needs Trust to preserve benefits eligibility for a disabled beneficiary.',
        "New York's trust law is codified in EPTL Article 7. The statute recognizes revocable trusts (EPTL § 7-1.16 default rule of irrevocability is now reversed for trusts created after 1997 — they are irrevocable unless the instrument expressly reserves the right to revoke), spendthrift trusts, charitable trusts, and the comparatively new EPTL § 7-1.9 modification and decanting provisions. Decanting allows a trustee to pour the assets of one irrevocable trust into a second trust with more favorable terms, providing flexibility that the original drafter may not have anticipated.",
      ],
    },
    subServices: [
      {
        title: 'Last Will & Testament Drafting',
        description:
          "We draft and execute wills that satisfy every requirement of EPTL § 3-2.1, with a self-proving affidavit signed at the same ceremony so the witnesses never have to be tracked down later. The will names primary and successor executors, addresses tangible personal property, exercises any powers of appointment, and funds testamentary trusts for minors or disabled beneficiaries.",
        bullets: [
          'EPTL § 3-2.1 execution formalities',
          'Self-proving affidavit at signing',
          'Successor executor and guardian designations',
          'Tangible personal property memorandum',
          'Testamentary trust provisions for minors',
        ],
      },
      {
        title: 'Revocable Living Trust',
        description:
          'A funded revocable trust avoids probate, keeps your asset inventory private, and gives a successor trustee immediate authority at incapacity — no court order, no waiting period. We draft the trust agreement, prepare the certificate of trust used to evidence authority to third parties, and (critically) walk you through funding: deeds, account retitling, beneficiary updates.',
        bullets: [
          'Trust agreement drafting',
          'Certificate of trust for third parties',
          'Deed preparation and real estate retitling',
          'Brokerage and bank account funding letters',
          'Coordinating pour-over will as backstop',
        ],
      },
      {
        title: 'Medicaid Asset Protection Trust (MAPT)',
        description:
          'A MAPT is an irrevocable trust designed to shelter assets from the Medicaid eligibility calculation. Assets transferred into a properly drafted MAPT at least 60 months (the SSL § 366 lookback) before a Medicaid application are protected from the institutional care spend-down and from post-death estate recovery. The grantor cannot serve as trustee and cannot retain access to principal.',
        bullets: [
          '5-year lookback under SSL § 366',
          'Income retained, principal locked',
          'Grantor cannot be trustee',
          'Children typically serve as trustees',
          'Step-up in basis preserved at death',
        ],
      },
      {
        title: 'Irrevocable Life Insurance Trust (ILIT)',
        description:
          'An ILIT owns your life insurance policy so the death benefit is not included in your taxable estate. Because the policy is owned outside the estate, the full death benefit passes to the beneficiaries free of federal and New York estate tax — frequently the largest single tax saving achievable in an otherwise estate-taxable plan. Crummey notice procedures convert annual premium gifts into excluded present-interest gifts.',
        bullets: [
          'Three-year lookback for transferred policies',
          'Crummey withdrawal rights',
          'Annual exclusion gifts for premium payments',
          'Spousal access via SLAT structure',
          'Generation-skipping (GST) allocation',
        ],
      },
      {
        title: 'Special Needs Trust',
        description:
          'A Special Needs Trust (SNT) holds assets for a disabled beneficiary in a way that preserves eligibility for SSI, Medicaid, and other means-tested benefits. A third-party SNT (funded by anyone other than the beneficiary) has no payback requirement; a first-party (self-settled) SNT funded with the beneficiary\'s own assets must include a Medicaid payback at death. Both are governed by EPTL Article 7 and federal 42 U.S.C. § 1396p.',
        bullets: [
          'Third-party SNT (no Medicaid payback)',
          'First-party (d)(4)(A) SNT with payback',
          'Pooled trust alternative (NYSARC)',
          'Distributions for supplemental needs only',
          'Trustee discretion to protect benefits',
        ],
      },
      {
        title: 'Charitable Remainder Trust (CRT)',
        description:
          'A CRT lets you transfer appreciated assets to a trust that pays you (or another beneficiary) a stream of income for life or a term of years, with the remainder to charity. The contribution is partially deductible as a charitable gift, the trust sells the appreciated asset without capital gains, and the income stream often exceeds what the original asset produced. CRUT and CRAT variations match different income needs.',
        bullets: [
          'CRUT (unitrust) — variable payout',
          'CRAT (annuity) — fixed payout',
          'Avoid capital gains on appreciated property',
          'Partial income tax deduction',
          'Income stream for life or term up to 20 years',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Trust Law — Key Facts',
      intro: 'The statutes and timelines that govern New York trusts and wills.',
      facts: [
        { label: 'Will execution', value: 'EPTL § 3-2.1 — 2 witnesses signing within 30 days' },
        { label: 'Trust types', value: 'EPTL Article 7 — revocable, irrevocable, charitable' },
        { label: 'Default rule', value: 'Trusts created after 1997 are irrevocable unless stated otherwise' },
        { label: 'MAPT lookback', value: '60 months under SSL § 366' },
        { label: 'Holographic wills', value: 'Valid only for active military per EPTL § 3-2.2' },
        { label: 'Community property', value: 'New York is NOT a community property state' },
        { label: 'Modification', value: 'EPTL § 7-1.9 decanting available to trustees' },
        { label: 'Spousal elective share', value: 'EPTL § 5-1.1-A — greater of $50K or 1/3' },
      ],
    },
    process: {
      headline: 'Our Wills & Trusts Process',
      intro: 'From discovery through funding, every step is built around the documents holding up.',
      steps: [
        {
          title: 'Discovery',
          description:
            'A free 30-minute consultation to understand your assets, family structure, and concerns. We confirm whether a will alone or a trust-based plan fits your situation.',
        },
        {
          title: 'Drafting',
          description:
            'First drafts within 14 to 21 days of engagement. We walk through the documents paragraph by paragraph; revisions are included in the flat fee.',
        },
        {
          title: 'Execution Ceremony',
          description:
            'Two qualified witnesses, a notary, and a self-proving affidavit are arranged at our office. The will is signed, witnessed, and notarized in one sitting.',
        },
        {
          title: 'Funding',
          description:
            'For trust-based plans we prepare deeds for real estate, retitling letters for brokerage and bank accounts, and beneficiary designation updates for retirement accounts and life insurance. An unfunded trust is just paper.',
        },
        {
          title: 'Periodic Review',
          description:
            'A complimentary review every three years and after every major life event. Statutory changes (POA reform, SECURE Act, anticipated 2026 federal sunset) regularly require updates.',
        },
      ],
    },
    faq: [
      {
        question: 'What is a pour-over will?',
        answer:
          'A pour-over will is the will companion to a revocable trust. Rather than distributing assets directly, it sweeps any property still in the testator\'s individual name at death into the existing trust, which then handles distribution under its own terms. It catches assets that were forgotten or acquired after the trust was funded.',
      },
      {
        question: 'Does a revocable trust avoid New York estate tax?',
        answer:
          'No. A revocable trust avoids probate but not estate tax. For both federal and New York estate-tax purposes, assets in a revocable trust are still part of your taxable estate at death. Estate-tax savings come only from irrevocable transfers — ILITs, MAPTs, SLATs, and the like.',
      },
      {
        question: 'Can I be my own trustee?',
        answer:
          'For a revocable trust, yes — you typically serve as your own trustee during your lifetime, with a named successor stepping in at incapacity or death. For an irrevocable trust like a MAPT or ILIT, no — the grantor cannot serve as trustee without compromising the tax or asset-protection benefits the trust is designed to deliver.',
      },
      {
        question: 'What is the 5-year Medicaid lookback?',
        answer:
          'Under SSL § 366, when a New Yorker applies for institutional (nursing home) Medicaid, the agency reviews the prior 60 months of transfers. Gifts and below-market transfers during that window create a penalty period that delays eligibility. Assets transferred into a MAPT more than 60 months before the application are outside the lookback and are not counted.',
      },
      {
        question: 'What is the difference between a revocable and irrevocable trust?',
        answer:
          'A revocable trust can be amended, restated, or revoked entirely by the grantor at any time. It offers probate avoidance and incapacity planning but no tax or creditor protection. An irrevocable trust cannot be modified except through narrow statutory procedures (decanting, court reformation, or trust protector mechanisms) and offers tax and creditor protection in exchange for that loss of control.',
      },
      {
        question: 'Can I change a trust I already created?',
        answer:
          'A revocable trust — yes, freely. An irrevocable trust — rarely, and only through specific mechanisms: decanting under EPTL § 7-1.9, judicial reformation, exercise of a trust protector\'s powers, or unanimous consent of grantor and all beneficiaries under the Restatement standard. Most modern irrevocable trusts include a trust protector provision specifically to allow for future changes.',
      },
      {
        question: 'What is decanting?',
        answer:
          'Decanting is the trustee\'s statutory power under EPTL § 7-1.9 to pour the assets of an existing irrevocable trust into a new trust with different (usually more favorable) terms. It is used to correct drafting errors, extend trust duration, change governing law, add a special needs provision, or modify trustee succession.',
      },
      {
        question: 'Do I need to retitle real estate into my revocable trust?',
        answer:
          'Yes. To avoid probate on a piece of New York real estate, the deed must be re-recorded in the name of the trust during your lifetime. This requires a new deed prepared and filed with the county clerk. Real estate held in your individual name at death is a probate asset regardless of what your trust agreement says.',
      },
      {
        question: 'How much does a basic will cost?',
        answer:
          'A simple will for a single individual with straightforward assets is one of our most economical engagements. More complex documents — wills with testamentary trusts, married-couple reciprocal wills, or trust-based plans — are quoted as a flat fee after the initial consultation. There are no hourly surprises and no per-page charges.',
      },
      {
        question: 'Should I name a trust or individuals as beneficiaries of my retirement accounts?',
        answer:
          'It depends on the family. Naming individuals provides the cleanest tax treatment and maximizes use of the SECURE Act 10-year payout rule. Naming a trust offers control (timing, protection from creditors or divorce, special needs preservation) but requires that the trust qualify as a "designated beneficiary" or "see-through" trust under the IRC § 401(a)(9) regulations. We coordinate this decision case by case.',
      },
    ],
    cta: {
      headline: 'Draft Your New York Will & Trust',
      body: 'Schedule a free 30-minute consultation with Russel Morgan, Esq. We will scope the documents you need, quote a flat fee, and start drafting — no obligation.',
    },
    related: ['estate-planning', 'probate', 'elder-law', 'asset-protection'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
  'asset-protection': {
    slug: 'asset-protection',
    url: '/asset-protection/',
    title: 'NYC Asset Protection Attorney | Trusts & LLCs | Morgan Legal',
    metaDescription:
      'Strategic legal structures that shield New York assets from creditors, lawsuits, and divorce. DAPTs, irrevocable trusts, LLC structures. Free consultation (888) 529-1315.',
    heroEyebrow: 'Asset Protection · NY DCL & EPTL',
    heroHeadline: 'Asset Protection Built for',
    heroHeadlineAccent: 'Modern New York',
    heroLead:
      'New York is one of the most litigious jurisdictions in the country, and its debtor-creditor statutes are correspondingly hostile to last-minute shelter. Morgan Legal Group designs and implements pre-claim asset protection strategies — out-of-state DAPTs, irrevocable trusts, LLC structures, and tenancy by the entirety — that work because they are built before a lawsuit ever arrives.',
    overview: {
      headline: 'Why Asset Protection Matters in New York',
      paragraphs: [
        'New York does not authorize self-settled Domestic Asset Protection Trusts (DAPTs) under its own law. A New Yorker who wants the protection of a DAPT must establish the trust under the law of a permissive jurisdiction — Delaware, Nevada, South Dakota, Alaska, or Wyoming — using an institutional trustee located in that state. The trust is governed by the situs state\'s creditor-friendly statutes, and New York courts will generally honor the choice-of-law provision absent fraud or public-policy violations.',
        'The single most important rule in asset protection is timing. Transfers made before any claim arises are usually defensible. Transfers made after a claim is threatened, even if no lawsuit has been filed, are vulnerable as fraudulent conveyances under New York Debtor & Creditor Law Article 10 (the 2020 Uniform Voidable Transactions Act enacted by New York). The statute of limitations for fraudulent transfer claims is four years from the transfer, or one year from discovery, whichever is later — a long window during which a structure can be unwound.',
        'Some New York assets are already protected by statute and do not require additional planning. The homestead exemption under CPLR § 5206 protects up to $179,975 of equity in a principal residence located in New York City, Nassau, Suffolk, Westchester, Rockland, and Putnam counties (and $149,975 in Albany, Columbia, Dutchess, Orange, Saratoga, Schenectady, and Ulster; $89,975 elsewhere). ERISA-qualified retirement accounts (401(k), defined benefit plans) enjoy a 100% federal shield. Life insurance with a named beneficiary other than the estate passes outside the reach of the insured\'s creditors. These statutory shields are the foundation; further planning builds on top of them.',
        'Different professions face different threats. Surgeons and OB/GYNs face medical malpractice exposure that can exceed insurance limits. Real estate developers face contractual personal guarantees and slip-and-fall liability on properties held in inadequate structures. Business owners face employment claims, contractual breach claims, and personal guarantees on commercial leases. Each profile drives a different plan — there is no single asset-protection template.',
      ],
    },
    subServices: [
      {
        title: 'Domestic Asset Protection Trust (DAPT)',
        description:
          'Because New York does not authorize self-settled DAPTs, we establish the trust under the law of a permissive state (most commonly Delaware, Nevada, or South Dakota) with a chartered trust company in that state as trustee. The grantor can be a discretionary beneficiary but never serves as trustee. After a state-specific statute of limitations (typically two to four years), the assets are largely beyond the reach of future creditors.',
        bullets: [
          'Out-of-state situs (DE, NV, SD, AK, WY)',
          'Institutional trustee in situs state',
          'Grantor as discretionary beneficiary',
          'Statute of limitations: 2-4 years (varies by state)',
          'Choice-of-law clause respected by NY courts',
        ],
      },
      {
        title: 'LLC & Corporate Structuring',
        description:
          'Properly structured LLCs limit liability arising from business activities and rental real estate. New York treats single-member LLCs more strictly than multi-member: a creditor of the sole member can in some cases reach LLC assets through a charging order or direct foreclosure on the membership interest. Multi-member LLCs and Series LLCs (where each series operates as a separate liability silo) provide stronger protection.',
        bullets: [
          'Multi-member operating agreements',
          'Series LLC for separated liability silos',
          'Charging-order limitations analysis',
          'Real estate LLC titling',
          'Holding company / subsidiary structures',
        ],
      },
      {
        title: 'Tenancy by the Entirety',
        description:
          'Married couples in New York can hold real estate as tenants by the entirety, which automatically shields the property from the individual creditors of either spouse. Only a creditor with a judgment against both spouses jointly can reach the property. This protection vanishes on divorce or death, but during the marriage it is one of the most effective shields available — and it is free.',
        bullets: [
          'Available only to legally married spouses',
          'Real property only (not personal property in NY)',
          'Joint creditors can reach; individual creditors cannot',
          'Automatic right of survivorship',
          'Lost on divorce, death, or partition',
        ],
      },
      {
        title: 'Retirement Account Optimization',
        description:
          'ERISA-qualified retirement plans (401(k), 403(b), defined benefit) enjoy a 100% federal anti-alienation shield under 29 U.S.C. § 1056(d). Traditional and Roth IRAs are protected under New York CPLR § 5205(c) and federal bankruptcy law up to inflation-adjusted limits (currently $1,512,350 for non-rollover IRAs). Rollovers from qualified plans into rollover IRAs preserve the unlimited federal shield.',
        bullets: [
          '401(k) / 403(b) — 100% ERISA shield',
          'Rollover IRA — unlimited federal shield',
          'Contributory IRA — capped at $1.5M (2024)',
          'Roth conversion analysis',
          'Inherited IRA protection (post-Clark)',
        ],
      },
      {
        title: 'Insurance-Based Protection',
        description:
          'Insurance is the first line of asset protection — every plan starts here. Personal umbrella policies typically extend $1 to $10 million of liability coverage at modest annual cost. Professional liability insurance covers malpractice and errors-and-omissions claims. For high net worth clients with concentrated risk, a captive insurance company can convert deductible premium dollars into accumulated reserves owned by the insured.',
        bullets: [
          'Personal umbrella ($1-$10M layered)',
          'Professional liability / E&O',
          'Directors & officers (D&O)',
          'Captive insurance company formation',
          'Coordinating coverage with structural shields',
        ],
      },
      {
        title: 'Pre-Marital & Post-Marital Agreements',
        description:
          'Under DRL § 236(B)(3), a properly drafted and executed prenuptial or postnuptial agreement can isolate premarital assets, inheritances, and business interests from equitable distribution in a future divorce. The agreement must be in writing, signed by both parties, acknowledged with the formality of a deed, and entered into without duress or unconscionability. We draft, negotiate, and execute these instruments with full financial disclosure.',
        bullets: [
          'DRL § 236(B)(3) execution formalities',
          'Full financial disclosure required',
          'Independent counsel for both parties',
          'Acknowledgment with deed-grade formality',
          'Post-marital agreements while still married',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Asset Protection — Key Facts',
      intro: 'The statutes that determine what works in New York and what does not.',
      facts: [
        { label: 'Self-settled DAPTs in NY', value: 'NOT permitted — out-of-state situs required' },
        { label: 'Fraudulent transfer statute', value: 'NY DCL Article 10 (UVTA, enacted 2020)' },
        { label: 'Fraudulent transfer SOL', value: '4 years from transfer / 1 year from discovery' },
        { label: 'Homestead exemption (NYC area)', value: '$179,975 per CPLR § 5206' },
        { label: 'Homestead exemption (upstate)', value: '$89,975 per CPLR § 5206' },
        { label: 'ERISA retirement shield', value: '100% federal (29 U.S.C. § 1056(d))' },
        { label: 'Tenancy by the entirety', value: 'Real property only, married couples only' },
        { label: 'IRA protection cap', value: '$1,512,350 (non-rollover, 2024)' },
      ],
    },
    process: {
      headline: 'Our Asset Protection Process',
      intro: 'A defined path from asset audit to implementation and annual review.',
      steps: [
        {
          title: 'Asset Audit',
          description:
            'A complete inventory: real estate, business interests, retirement accounts, taxable investments, life insurance, and personal property. We identify what is already protected by statute and what is exposed.',
        },
        {
          title: 'Risk Assessment',
          description:
            'We map the threats specific to your profession, business activities, and personal life — professional liability, contractual exposure, divorce risk, and inheritance claims. The plan is calibrated to the actual risks, not generic templates.',
        },
        {
          title: 'Structure Design',
          description:
            'We propose a layered structure: insurance first, statutory shields second, transactional structures (LLCs, partnerships) third, and trusts (domestic and out-of-state) fourth. Each layer is selected for the marginal protection it adds beyond the layer beneath.',
        },
        {
          title: 'Implementation',
          description:
            'Trust agreements drafted and signed, LLCs formed and operating agreements executed, real estate retitled, insurance bound, account beneficiaries updated. Implementation is sequenced to avoid creating fraudulent-transfer issues during the buildout.',
        },
        {
          title: 'Annual Review',
          description:
            'Each year we reconfirm that the structure still matches the asset base, that insurance is in force, and that no new threats or jurisdictional changes require adjustment.',
        },
      ],
    },
    faq: [
      {
        question: 'Can I shield assets after a lawsuit has been filed?',
        answer:
          'Almost never. Transfers made after a claim is threatened or filed are vulnerable as fraudulent conveyances under NY DCL Article 10. The statute of limitations runs four years from the transfer (or one year from discovery), giving a creditor a long window to unwind the structure. Effective asset protection is pre-claim — built years before any threat materializes.',
      },
      {
        question: 'Are New York Domestic Asset Protection Trusts allowed?',
        answer:
          'No. New York does not authorize self-settled DAPTs under its own law. A New Yorker who wants DAPT protection must establish the trust under the law of a permissive state (Delaware, Nevada, South Dakota, Alaska, or Wyoming) with an institutional trustee in that state. New York courts will generally honor the choice-of-law clause absent fraud or public-policy violations.',
      },
      {
        question: 'Is my retirement account protected from creditors in New York?',
        answer:
          'Largely yes. ERISA-qualified plans (401(k), 403(b), defined benefit) enjoy a 100% federal anti-alienation shield. Rollover IRAs preserve the unlimited federal protection. Contributory IRAs are protected up to $1,512,350 (2024) under federal bankruptcy law and CPLR § 5205(c) provides additional New York protection outside bankruptcy. Inherited IRAs are not protected post-Clark v. Rameker.',
      },
      {
        question: 'What is tenancy by the entirety?',
        answer:
          "Tenancy by the entirety is a form of joint ownership available only to legally married spouses in New York, applicable only to real property. Each spouse owns 100% of the property; neither can convey without the other; and creditors of one spouse alone cannot reach the property. Joint creditors of both spouses can reach it. The protection ends at divorce or the death of either spouse.",
      },
      {
        question: 'How much equity in my New York home is protected?',
        answer:
          'Under CPLR § 5206, the homestead exemption is $179,975 in New York City, Nassau, Suffolk, Westchester, Rockland, and Putnam counties; $149,975 in Albany, Columbia, Dutchess, Orange, Saratoga, Schenectady, and Ulster; and $89,975 elsewhere in New York State. The protection applies to the principal residence and runs against unsecured judgment creditors — not mortgages, tax liens, or mechanic\'s liens.',
      },
      {
        question: 'Does an LLC protect me personally from business liabilities?',
        answer:
          'Generally yes — a properly structured and properly operated LLC limits liability for business debts and tort claims to the assets of the LLC itself. However, single-member LLCs in New York receive less protection than multi-member LLCs because a creditor of the sole member can in some cases foreclose on the membership interest directly. Multi-member LLCs and Series LLCs are stronger structures.',
      },
      {
        question: 'When should I start asset protection planning?',
        answer:
          'Before you need it. The whole architecture depends on transfers being made well before any claim arises. For a physician, that often means before residency ends. For a business owner, before the company has meaningful value. For a high net worth investor, immediately. By the time a claim is on the horizon, most planning options are foreclosed by fraudulent-transfer rules.',
      },
      {
        question: 'Will the IRS or a divorcing spouse pierce my asset protection structure?',
        answer:
          'The IRS has unique collection powers and asset-protection trusts generally do not defeat federal tax liens. A divorcing spouse is subject to DRL equitable distribution, and a court can in many cases unwind a transfer made during the marriage. Asset protection works best against unsecured commercial and tort creditors — not against the IRS, not against an ex-spouse, and not against a criminal restitution order.',
      },
      {
        question: 'Can I be a beneficiary of my own asset-protection trust?',
        answer:
          'In a DAPT established under the law of a permissive state (Delaware, Nevada, South Dakota), yes — the grantor can be a discretionary beneficiary while still receiving creditor protection after the situs state\'s statute of limitations runs. In a domestic New York-law trust, no — a self-settled trust does not shield the grantor under New York law.',
      },
      {
        question: 'How long does New York give a creditor to attack a transfer as fraudulent?',
        answer:
          'Under NY DCL Article 10, a creditor may bring a fraudulent-transfer action within four years of the transfer, or one year after the transfer is discovered by the creditor, whichever is later. This long window is one of the reasons asset protection must be built well in advance — a transfer made shortly before a claim arises is exposed for years.',
      },
    ],
    cta: {
      headline: 'Protect Your New York Assets',
      body: 'Schedule a free 30-minute consultation with Russel Morgan, Esq. We will map your exposure, identify what is already protected, and design the structure that fits your risk profile.',
    },
    related: ['estate-planning', 'trusts', 'elder-law', 'real-estate'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
  guardianship: {
    slug: 'guardianship',
    url: '/guardianship/',
    title: 'NYC Article 81 Guardianship Attorney | MHL & SCPA 17-A | Morgan Legal',
    metaDescription:
      'Article 81 guardianships for incapacitated New York adults. SCPA 17-A for developmentally disabled. Standby guardianships, court-appointed counsel. (888) 529-1315.',
    heroEyebrow: 'Guardianship · MHL Article 81',
    heroHeadline: 'Compassionate Guardianship for',
    heroHeadlineAccent: 'New York Families',
    heroLead:
      'When a loved one in New York can no longer manage their affairs, the right guardianship petition is the difference between months of court delay and a workable, narrowly tailored solution. Morgan Legal Group handles both MHL Article 81 adult incapacity guardianships and SCPA Article 17-A developmental disability guardianships across all five boroughs and Long Island.',
    overview: {
      headline: 'Guardianship in New York — Three Distinct Tracks',
      paragraphs: [
        "New York does not use a single guardianship statute. The framework is split across three tracks, and choosing the wrong one wastes months. Mental Hygiene Law Article 81 governs adult incapacity guardianships — the petition used when an aging parent’s dementia, a stroke, or a traumatic brain injury has left them unable to manage property or personal needs. Article 81 is deliberately built around the principle of least restrictive alternative: the court grants only the specific powers the proof shows are necessary, leaving every other right with the alleged incapacitated person (AIP).",
        "Surrogate’s Court Procedure Act Article 17 covers guardianships of the property and person of minors under the age of 18. It is the vehicle used when a child inherits money, receives a personal-injury settlement, or has been orphaned. Article 17-A is the entirely separate track for adults whose intellectual or developmental disability began before age 22 — adults with Down syndrome, severe autism, traumatic brain injury suffered in childhood, or cerebral palsy. Article 17-A is functionally plenary (broader powers, easier to obtain) but available only to a narrow population and only on the certification of two physicians or one physician plus one psychologist.",
        "Article 81 proceedings are demanding by design. The petition must allege functional limitations with specificity, propose the powers requested with a clear nexus to those limitations, and identify the resources the guardian will need to manage. The court appoints a court evaluator under MHL § 81.09 to interview the AIP and report independently to the judge. The AIP is entitled to court-appointed counsel under MHL § 81.10 — appointment is mandatory unless the AIP retains their own attorney. A hearing is required, and the AIP has the right to attend, testify, and confront witnesses.",
        "The cost of getting it wrong is not just the lost filing fee. A guardianship granted with the wrong powers can leave critical decisions outside the guardian’s authority; one granted with overbroad powers strips a person of rights they were capable of exercising. Our practice is built around the careful surgical work of asking for exactly what is needed — and proving it.",
      ],
    },
    subServices: [
      {
        title: 'Article 81 Petition — Adult Incapacity',
        description:
          'We prepare and file the MHL § 81.06 petition with allegations of functional limitations, the proposed powers tied to each limitation, and a comprehensive resource list. The order to show cause is presented with a proposed schedule that respects the 28-day hearing window required by MHL § 81.07.',
        bullets: [
          'MHL § 81.06 petition with specific functional findings',
          'Order to show cause and proposed hearing schedule',
          'Notice to interested parties per MHL § 81.07',
          'Coordination with court evaluator (MHL § 81.09)',
          'Proposed powers narrowly tailored to proof',
        ],
      },
      {
        title: 'Article 17-A Petition — Developmental Disability',
        description:
          'For adults whose intellectual or developmental disability began before age 22, we file the SCPA Article 17-A petition in Surrogate’s Court. The petition is supported by the statutorily required certifications — either two physicians or one physician and one licensed psychologist — and identifies the proposed guardian of the person, of the property, or both.',
        bullets: [
          'Two-physician or physician+psychologist certifications',
          'Guardian of person and/or property designations',
          'Standby and successor guardian nominations',
          'Annual reporting setup for property guardianships',
          'Coordination with OPWDD service providers',
        ],
      },
      {
        title: 'Standby Guardianship — SCPA § 1726',
        description:
          'For a parent with a terminal diagnosis, a serious chronic illness, or progressive incapacity, a standby guardianship under SCPA § 1726 lets the parent designate a guardian whose authority springs into effect automatically on the parent’s death, incapacity, or written consent. The standby guardian can act immediately without a new court proceeding at the critical moment.',
        bullets: [
          'Triggering events: death, incapacity, or consent',
          'Immediate authority without re-petition',
          'Designation must be filed with the court within 60 days',
          'Springing authority preserves parental control until triggered',
          'Common for HIV+ parents, cancer patients, progressive illness',
        ],
      },
      {
        title: 'Annual Reporting & Inventory',
        description:
          'A guardian’s job does not end at the order. Within 90 days of appointment, the guardian must file an initial inventory of the IP’s assets. Each year, by May 31 (or by the appointment anniversary if the court so orders), the guardian must file the annual report required by MHL § 81.31 covering personal and financial status. We prepare these filings, defend objections, and shepherd commissions calculations.',
        bullets: [
          '90-day initial inventory after appointment',
          'Annual MHL § 81.31 report covering person and property',
          'Court accounting for property guardians',
          'Coordination with surety on bond requirements',
          'Final accounting on termination',
        ],
      },
      {
        title: 'Guardian ad Litem Appointments',
        description:
          'In probate, accountings, real estate sales, and infant compromise proceedings, the court appoints a guardian ad litem (GAL) to represent the interests of an under-age, incapacitated, or unknown party. We accept GAL appointments from Surrogate’s Court and Supreme Court across the five boroughs and Long Island, and we appear opposite GALs in proceedings where our clients are the active parties.',
        bullets: [
          'Surrogate’s Court GAL appointments',
          'Infant compromise hearings',
          'Real estate sale proceedings',
          'Probate and accounting representation',
          'Investigation, reporting, and recommendation to court',
        ],
      },
      {
        title: 'Contested Guardianship Litigation',
        description:
          'Not every guardianship petition is welcome. The AIP may oppose the petition, family members may dispute the choice of guardian, and rival petitions are common in fractured families. We represent both petitioners and objectants — gathering medical proof, taking depositions, conducting cross-examination of the court evaluator, and presenting the case at a contested hearing.',
        bullets: [
          'Petitioner-side trial preparation',
          'AIP-side opposition (through retained counsel)',
          'Rival-petitioner disputes',
          'Court evaluator deposition and cross-examination',
          'Capacity expert retention and testimony',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Guardianship — Key Facts',
      intro: 'The statutes, standards, and timelines that govern every New York guardianship.',
      facts: [
        { label: 'Article 81 enacted', value: '1992 — replaced Article 77 conservatorships' },
        { label: 'Standard of proof', value: 'Clear and convincing evidence of incapacity + need' },
        { label: 'Court evaluator', value: 'Mandatory in every Article 81 case (MHL § 81.09)' },
        { label: 'AIP counsel', value: 'Mandatory court-appointed unless retained (MHL § 81.10)' },
        { label: 'Article 17-A certification', value: '2 physicians OR 1 physician + 1 psychologist' },
        { label: 'Initial inventory', value: 'Due 90 days from appointment' },
        { label: 'Annual report', value: 'Due May 31 / appointment anniversary (MHL § 81.31)' },
        { label: 'Hearing window', value: 'Generally within 28 days of OSC issuance' },
      ],
    },
    process: {
      headline: 'Our Guardianship Process',
      intro: 'From the first family meeting to the discharge order — a defined, calm path.',
      steps: [
        {
          title: 'Initial Consultation',
          description:
            'A free 30-minute call with the family to understand the AIP’s condition, the assets at risk, and the family dynamics. We confirm whether Article 81, Article 17-A, or a non-guardianship alternative (power of attorney, health care proxy, supported decision-making) is the right tool.',
        },
        {
          title: 'Petition & Order to Show Cause',
          description:
            'We draft the petition with functional findings tied to specific powers, prepare the order to show cause, and file with the appropriate Supreme Court (Article 81) or Surrogate’s Court (Article 17-A). Service on the AIP and all interested parties follows.',
        },
        {
          title: 'Court Evaluator & Counsel Appointment',
          description:
            'The court appoints a court evaluator and (in Article 81 cases) counsel for the AIP. We coordinate the evaluator’s access to medical records and interviews, and we cooperate with AIP’s counsel on scheduling and disclosure.',
        },
        {
          title: 'Hearing',
          description:
            'A hearing is held generally within 28 days of the OSC. We present medical proof, financial proof, and witness testimony establishing incapacity and the need for the powers requested. The AIP may attend, testify, and oppose.',
        },
        {
          title: 'Order & Commission',
          description:
            'The court issues findings of fact, conclusions of law, and an order granting the specific powers proven necessary. A commission issues to the guardian; banks and providers will recognize it immediately.',
        },
        {
          title: 'Reporting & Closure',
          description:
            'We prepare the 90-day inventory, the annual report, and (where required) the property accounting. When the guardianship ends — by death, restoration of capacity, or final accounting — we shepherd the discharge order.',
        },
      ],
    },
    faq: [
      {
        question: 'When do I need an Article 81 guardianship instead of a power of attorney?',
        answer:
          'A power of attorney is only effective if it was signed while the principal had capacity. Once capacity is lost and no valid POA exists, the only path forward is a guardianship proceeding. Even when an old POA exists, banks may refuse to honor it for major real estate transactions or contested decisions, forcing the family into Article 81. The single best prevention is executing a current statutory short-form POA while the principal is healthy.',
      },
      {
        question: 'What is the difference between Article 81 and Article 17-A?',
        answer:
          'Article 81 is the adult incapacity statute — designed for people who once had capacity and lost it (dementia, stroke, traumatic brain injury). Article 17-A is the developmental disability statute — designed for adults whose intellectual or developmental disability began before age 22 (Down syndrome, severe autism, congenital brain injury). The proof, the procedure, and the powers granted differ significantly between the two tracks.',
      },
      {
        question: 'How long does an Article 81 guardianship take?',
        answer:
          'An uncontested Article 81 proceeding typically takes 60 to 120 days from filing to the issuance of the commission. The hearing is generally held within 28 days of the order to show cause, but securing a court evaluator report, completing service, and obtaining the bond can extend the timeline. Contested matters can take six months or more.',
      },
      {
        question: 'Can a guardianship petition be contested?',
        answer:
          'Yes. The AIP has the right to oppose the petition entirely, to oppose the powers requested, or to object to the proposed guardian. Family members may file rival petitions or object to the petitioner’s choice. The AIP is entitled to court-appointed counsel under MHL § 81.10, and a contested hearing can include witnesses, expert testimony, and cross-examination of the court evaluator.',
      },
      {
        question: 'What powers can a guardian receive?',
        answer:
          'Article 81 powers are tailored — the court grants only what is proven necessary. Common personal needs powers include making medical decisions, choosing a residence, and arranging services. Common property management powers include managing accounts, paying bills, applying for benefits, and (with specific authorization) selling real estate. The court can also grant the power to consent to a major medical procedure, the power to make a gift, or the power to revoke a prior power of attorney.',
      },
      {
        question: 'Do I need a lawyer to file a guardianship petition?',
        answer:
          'The court will accept a pro se petition, but the procedural complexity (functional findings tied to powers, court evaluator coordination, evidence at hearing) and the AIP’s right to court-appointed counsel make attorney representation strongly advisable. The petitioner generally retains their own counsel; the AIP receives mandatory court-appointed counsel under MHL § 81.10.',
      },
      {
        question: 'What is the standard of proof?',
        answer:
          'Clear and convincing evidence — a higher standard than the preponderance-of-the-evidence used in civil cases, lower than the beyond-a-reasonable-doubt standard used in criminal cases. The petitioner must establish both that the AIP is incapacitated and that a guardian is necessary to manage personal needs or property management functions.',
      },
      {
        question: 'Can the alleged incapacitated person attend the hearing?',
        answer:
          'Yes. The AIP has the right to attend, to testify, to confront witnesses, and to be represented by court-appointed or retained counsel. The court will arrange transport and accommodations as needed. If the AIP cannot attend due to medical condition, the court may hold the hearing at the AIP’s residence or by remote appearance.',
      },
      {
        question: 'What is a standby guardianship?',
        answer:
          'A standby guardianship under SCPA § 1726 lets a parent or current guardian designate a successor whose authority springs into effect automatically on the designator’s death, incapacity, or written consent. It is common for parents with terminal diagnoses or progressive illness — the child is protected without the disruption of a new guardianship proceeding at the critical moment.',
      },
      {
        question: 'How much does an Article 81 guardianship cost?',
        answer:
          'Court filing fees scale with the size of the AIP’s assets, but most filings fall between $35 and $1,250. Attorney fees vary by complexity; Morgan Legal Group offers flat-fee engagements for uncontested matters and transparent hourly billing for contested proceedings. The court evaluator is paid from the AIP’s assets, as is court-appointed counsel for the AIP — both at court-approved rates.',
      },
    ],
    cta: {
      headline: 'Get Compassionate Guardianship Counsel',
      body: 'Schedule a free 30-minute consultation with Russel Morgan, Esq. We will assess capacity, review alternatives, and quote a transparent fee — no obligation.',
    },
    related: ['elder-law', 'estate-planning', 'power-of-attorney', 'trusts'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
  'power-of-attorney': {
    slug: 'power-of-attorney',
    url: '/power-of-attorney/',
    title: 'NYC Power of Attorney Lawyer | NY Statutory Short Form | Morgan Legal',
    metaDescription:
      'Properly executed New York powers of attorney under the June 13 2021 statutory short form (GOL Article 5 Title 15). Monetary gift rider, healthcare proxy. (888) 529-1315.',
    heroEyebrow: 'Power of Attorney · GOL Art. 5 Title 15',
    heroHeadline: 'New York Power of Attorney,',
    heroHeadlineAccent: 'Done by the Book',
    heroLead:
      'The 2021 New York POA reform tightened execution requirements but also gave principals real leverage against banks that refuse a valid form. Morgan Legal Group drafts powers of attorney that satisfy every formality of GOL Article 5 Title 15 — and that hold up when a teller, a hospital, or a county clerk pushes back.',
    overview: {
      headline: 'The New York Statutory Short Form Power of Attorney',
      paragraphs: [
        'A power of attorney is the most important legal document most people will execute during their lifetime. It controls who can act on your behalf if you cannot — paying bills, accessing accounts, applying for Medicaid, signing real estate documents, defending litigation. New York completely overhauled its statutory short form effective June 13, 2021, with the express dual goal of making the form harder to execute carelessly but also harder for third parties to reject without consequence.',
        'The reform consolidated execution into a single signing ceremony. The principal signs in the presence of a notary, and each agent (and each successor agent) must also acknowledge — that is, sign before a notary — before exercising authority. The separate "Statutory Gifts Rider" used under the prior 2009 form was eliminated; instead, any modification permitting gifts to anyone (including the agent) beyond $5,000 cumulative per calendar year must be included in the form’s built-in Modifications section.',
        'The new statute also introduced a "reasonable cause" standard for rejection under GOL § 5-1504. A third party that refuses a properly executed POA must give written notice of the reason within 10 business days. A court can award the principal actual damages plus attorney’s fees for an unreasonable refusal — a remedy that, for the first time, gives banks a real incentive to accept the form rather than send the client back for a "house form."',
        'A POA covers financial and legal authority. Healthcare decisions travel on a separate document — the Healthcare Proxy under Public Health Law § 2980. End-of-life treatment wishes are typically memorialized in a Living Will, and for patients with serious advanced illness, a MOLST (Medical Orders for Life-Sustaining Treatment) form signed by a physician gives EMS and hospital staff actionable orders. A complete plan executes all four in coordination.',
      ],
    },
    subServices: [
      {
        title: 'Statutory Short Form POA Drafting',
        description:
          'We draft and execute the post-2021 NY statutory short form, with the precise authority sections initialed by the principal that match the actual planning need. We coordinate principal-and-agent acknowledgment in a single signing, designate primary and successor agents, and decide whether multiple agents act jointly (unanimous) or independently (any agent alone).',
        bullets: [
          '2021 statutory short form (post 6/13/2021)',
          'General or limited authority sections',
          'Joint vs. independent multiple agents',
          'Primary and successor agent designations',
          'Acknowledgment ceremony in our office',
        ],
      },
      {
        title: 'Monetary Gift Modifications',
        description:
          'The 2021 form’s default ceiling on agent-made gifts is $5,000 per calendar year cumulative across all donees. Any plan that involves Medicaid spend-down gifts, family annual-exclusion gifts, or charitable gifts above that threshold requires custom modifications language in the form. We draft these provisions with the precision needed to survive bank scrutiny.',
        bullets: [
          '$5,000 cumulative annual default ceiling',
          'Custom modifications for Medicaid gifting',
          'Family annual-exclusion gift authority',
          'Charitable gift authority',
          'Self-gifting authority (agent as donee)',
        ],
      },
      {
        title: 'Springing vs. Immediate POA',
        description:
          'An immediate POA is effective the moment it is executed and the agent acknowledges. A springing POA becomes effective only on a defined trigger — typically a physician’s certification of incapacity. Springing forms feel more comfortable to many principals but are more cumbersome to use; banks demand documentation of the trigger before recognizing the agent’s authority. We help clients choose between the two based on family dynamics and asset structure.',
        bullets: [
          'Immediate POA — effective on signing',
          'Springing POA — effective on triggering event',
          'Triggering event drafting (single physician vs. two)',
          'Capacity certification logistics',
          'Trade-offs explained at consultation',
        ],
      },
      {
        title: 'Healthcare Proxy under PHL § 2980',
        description:
          'A Healthcare Proxy names an agent to make medical decisions when the principal lacks capacity. New York separates medical and financial authority by statute — the financial POA does not reach medical decisions. The Healthcare Proxy is signed in the presence of two adult witnesses (not the agent) and is effective on the attending physician’s determination that the principal lacks capacity to make the relevant decision.',
        bullets: [
          'PHL § 2980 form drafting',
          'Two-witness execution (not the agent)',
          'HIPAA authorization integration',
          'Out-of-state portability considerations',
          'Coordination with Living Will and MOLST',
        ],
      },
      {
        title: 'Living Will & MOLST',
        description:
          'A Living Will is a written statement of the principal’s wishes regarding life-sustaining treatment — ventilators, feeding tubes, CPR. It is not a statutory form in New York but is recognized at common law and is used by hospital ethics committees and the Healthcare Proxy agent. A MOLST is a physician’s order — completed and signed by a treating physician — that translates patient wishes into actionable medical orders for EMS and hospital staff.',
        bullets: [
          'Living Will preference drafting',
          'MOLST coordination with treating physician',
          'DNR / DNI orders for advanced illness',
          'Religious and conscientious wishes',
          'Out-of-state portability review',
        ],
      },
      {
        title: 'Bank Refusal Litigation — GOL § 5-1504',
        description:
          'When a bank or other third party refuses to honor a properly executed POA, GOL § 5-1504 gives the principal (and the agent) a private right of action. The statute requires the bank to provide written notice of the reason for refusal within 10 business days. If the refusal is unreasonable, the court may award the principal actual damages plus attorney’s fees. We send the demand, file the petition, and recover the fees.',
        bullets: [
          '10-business-day written reason requirement',
          'Actual damages plus attorney’s fees recovery',
          'Special proceeding under GOL § 5-1510',
          'Demand letter and informal resolution first',
          'Petition to court for unreasonable refusal',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Power of Attorney — Key Facts',
      intro: 'Dates, dollar thresholds, and statutes that govern every New York POA.',
      facts: [
        { label: 'Current form effective', value: 'June 13, 2021' },
        { label: 'Governing statute', value: 'NY GOL Article 5 Title 15' },
        { label: 'Notarization', value: 'Required for principal AND each agent' },
        { label: 'Gift ceiling (default)', value: '$5,000 cumulative per calendar year' },
        { label: 'Bank refusal — written reason', value: 'Required within 10 business days (GOL § 5-1504)' },
        { label: 'Damages for wrongful refusal', value: 'Actual damages + attorney’s fees' },
        { label: 'Healthcare proxy statute', value: 'NY PHL § 2980 (separate document)' },
        { label: 'Old forms', value: 'Pre-2021 forms remain valid if properly executed' },
      ],
    },
    process: {
      headline: 'Our POA Process',
      intro: 'From the scoping call to the signing ceremony — typically under three weeks.',
      steps: [
        {
          title: 'Consultation & Scope',
          description:
            'A free 30-minute call to identify the authority needed (real estate, tax, business, Medicaid, gifting), the right agents, and the appropriate trigger structure (immediate vs. springing). We also confirm whether a Healthcare Proxy and Living Will should be executed in the same package.',
        },
        {
          title: 'Drafting',
          description:
            'First draft within 7 to 14 days, including any modifications section needed for gifting above the $5,000 default. We walk through the document paragraph by paragraph in a review meeting.',
        },
        {
          title: 'Execution Ceremony',
          description:
            'Principal and each agent sign in the same ceremony in our office before a notary. Two adult witnesses are arranged for the Healthcare Proxy (which has different witness rules). The original document is delivered, with certified copies for the agents.',
        },
        {
          title: 'Agent Receipt & Use',
          description:
            'We brief each agent on what the POA permits and prohibits, the duty of care, the recordkeeping obligation, and the consequences of misuse. Many failures of POAs are failures of agent education, not failures of drafting.',
        },
        {
          title: 'Periodic Review',
          description:
            'A complimentary review every three to five years, after every major life event, and after any statutory change. Old POAs remain valid under the law in effect when they were signed, but newer planning needs sometimes require a fresh form.',
        },
      ],
    },
    faq: [
      {
        question: 'Is the 2021 form mandatory for new POAs?',
        answer:
          'Yes. Any POA executed on or after June 13, 2021 must use the new statutory short form under GOL Article 5 Title 15. POAs executed before that date remain valid under the law in effect when they were signed, but new POAs cannot use the old form. If you have an older POA, it is generally still valid — but a refresh is often appropriate to capture the new bank-refusal protections.',
      },
      {
        question: 'What is the monetary gift modifications section?',
        answer:
          'The 2021 form’s default ceiling on agent-made gifts is $5,000 cumulative per calendar year across all donees. To permit anything more — annual-exclusion gifts to family, Medicaid spend-down gifts, charitable gifts beyond the floor — the form’s built-in Modifications section must include specific language authorizing the larger amount and identifying the permitted donees. This replaces the separate "Statutory Gifts Rider" used under the prior 2009 form.',
      },
      {
        question: 'Do I need a separate Healthcare Proxy?',
        answer:
          'Yes. New York separates financial and medical authority by statute. The POA under GOL Article 5 Title 15 covers financial and legal decisions; medical decisions travel on a separate Healthcare Proxy under PHL § 2980. The two documents are typically executed together as part of a coordinated plan but are legally distinct, with different execution formalities (the Healthcare Proxy requires two adult witnesses rather than notarization).',
      },
      {
        question: 'Can my agent be sued for misusing the POA?',
        answer:
          'Yes. An agent owes a fiduciary duty to the principal and is personally liable for acts taken in bad faith or in breach of that duty. The 2021 form includes an "Important Information for the Agent" section that puts the agent on notice. Common claims include self-dealing (using the principal’s funds for the agent’s benefit), conversion, and breach of the duty to act in accordance with the principal’s reasonable expectations.',
      },
      {
        question: 'What if a bank refuses to accept my POA?',
        answer:
          'Under GOL § 5-1504, a bank that refuses a properly executed statutory short-form POA must give written notice of the reason for the refusal within 10 business days. If the refusal is unreasonable, the principal can bring a special proceeding under GOL § 5-1510 and recover actual damages plus attorney’s fees. The remedy is meaningful — many refusals are reversed once the bank receives a demand letter citing the statute.',
      },
      {
        question: 'Can I have multiple agents?',
        answer:
          'Yes. Multiple agents can be named to act jointly (all must agree on every action — strongest oversight but operationally difficult) or independently (any one agent can act alone — most flexible but least oversight). Successor agents can also be named in priority order to step in if the primary agent dies, resigns, or becomes incapable. The form requires the principal to specify which structure applies.',
      },
      {
        question: 'What is a springing power of attorney?',
        answer:
          'A springing POA becomes effective only when a defined triggering event occurs — typically a physician’s certification of the principal’s incapacity. Many principals prefer the comfort of a POA that does not give the agent immediate authority while the principal is still healthy. The trade-off is operational: banks routinely demand proof of the trigger before recognizing the agent’s authority, which can create delay at exactly the moment the agent needs to act.',
      },
      {
        question: 'Does a POA end at death?',
        answer:
          'Yes. A POA terminates instantly at the principal’s death — without exception. The moment the principal dies, the agent’s authority is gone, and the executor named in the will (or an administrator appointed by the Surrogate’s Court) takes over. Any transaction the agent attempts to complete after death is unauthorized, even if the agent acted in good faith and did not yet know of the death.',
      },
      {
        question: 'Can a POA be used to buy real estate for me?',
        answer:
          'Yes, if the form grants real estate authority and the agent has been acknowledged before a notary. The acknowledged POA must be recorded with the county clerk to be effective in conveying real property under Real Property Law § 294. We handle the recording in coordination with the closing.',
      },
      {
        question: 'How long does a POA stay valid?',
        answer:
          'Indefinitely, until revoked by the principal in writing or terminated by the principal’s death. The 2021 form is "durable" by default, meaning it survives the principal’s subsequent incapacity. Banks sometimes balk at older POAs; the GOL § 5-1504(2) provides that a POA may not be rejected solely because it is more than six months old, but in practice a fresh form smooths the transaction.',
      },
    ],
    cta: {
      headline: 'Execute Your New York Power of Attorney',
      body: 'Schedule a free 30-minute consultation with Russel Morgan, Esq. We will draft, review, and execute the right POA package — usually in under three weeks.',
    },
    related: ['estate-planning', 'elder-law', 'guardianship', 'trusts'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
  'elder-law': {
    slug: 'elder-law',
    url: '/elder-law/',
    title: 'NYC Elder Law Attorney | Medicaid & 5-Year Lookback | Morgan Legal',
    metaDescription:
      'New York elder law — Medicaid asset protection (5-year lookback per SSL § 366), long-term care planning, MAPT trusts, community vs institutional. (888) 529-1315.',
    heroEyebrow: 'Elder Law · SSL § 366 & MHL',
    heroHeadline: 'Elder Law for the',
    heroHeadlineAccent: 'Long Run',
    heroLead:
      'A skilled-nursing-facility stay in New York City now runs $15,000 to $18,000 a month — enough to drain a working family’s lifetime savings in under three years. Morgan Legal Group designs Medicaid plans that protect the family home, qualify the applicant under the institutional or community track, and time the five-year lookback correctly.',
    overview: {
      headline: 'Elder Law in New York',
      paragraphs: [
        "New York Medicaid is administered by the Department of Social Services and governed by Social Services Law § 366. The single most consequential rule is the 60-month (five-year) lookback for institutional Medicaid — any uncompensated transfer made in the 60 months before an institutional Medicaid application creates a transfer penalty, calculated by dividing the value of the transferred asset by the regional monthly cost of care to determine the number of months of ineligibility. Time, in elder law, is the asset that matters most.",
        "Community Medicaid — the program that pays for home-based personal care, certified home health agency services, and the Consumer Directed Personal Assistance Program (CDPAP) — operates differently. New York passed a 30-month lookback for community Medicaid in 2020, but enforcement has been delayed repeatedly through the state budget process and, as of 2024, the community lookback is still not being enforced. This unusually generous window has driven a substantial volume of last-minute community Medicaid planning that would not survive institutional Medicaid rules.",
        "The most reliable structural tool is the Medicaid Asset Protection Trust (MAPT) — an irrevocable trust into which the client transfers the family home and liquid assets at least 60 months before any institutional Medicaid application. The grantor cannot serve as trustee and cannot retain access to principal, but typically retains the right to income generated by the trust assets and the right to live in the home. Children commonly serve as trustees. At the grantor’s death, the trust assets pass to the beneficiaries with a stepped-up basis under IRC § 1014.",
        "Spousal protections soften the rule when only one spouse needs institutional care. The Community Spouse Resource Allowance (CSRA) shelters between $74,820 (the 2024 floor) and $154,140 (the 2024 ceiling) of the couple’s combined resources for the spouse remaining at home. The Minimum Monthly Maintenance Needs Allowance (MMMNA) — $3,853.50 per month in 2024 — guarantees the community spouse a minimum monthly income. Spousal refusal is also recognized in New York, allowing a community spouse to formally refuse to contribute to the institutionalized spouse’s care.",
      ],
    },
    subServices: [
      {
        title: 'Medicaid Asset Protection Trust (MAPT)',
        description:
          'A MAPT is an irrevocable trust drafted to remove the family home and liquid assets from the Medicaid eligibility calculation after the 60-month lookback. Drafted correctly, the trust preserves the grantor’s right to live in the home, receive trust income, and pass the assets to the beneficiaries at death with a stepped-up basis. Drafted incorrectly, it can defeat its own purpose — we have seen MAPTs that triggered immediate ineligibility because the grantor retained too much control.',
        bullets: [
          '60-month lookback timing (start drafting at 60-65)',
          'Income-only retention for grantor',
          'Children typically serve as trustees',
          'Principal locked from grantor access',
          'Stepped-up basis at death (IRC § 1014)',
        ],
      },
      {
        title: 'Medicaid Application & Recertification',
        description:
          'A Medicaid application is a document-intensive proceeding — five years of bank statements, deed history, transfer documentation, retirement and pension verification, current income, and a documented explanation for every notable transaction. We prepare and file the application, attend the local DSS interview, respond to requests for additional information, and shepherd the case through approval. Annual recertification follows the same rhythm with reduced documentation.',
        bullets: [
          'Initial application preparation and filing',
          '60-month financial document assembly',
          'DSS interview attendance and follow-up',
          'Response to deficiency requests',
          'Annual recertification packages',
        ],
      },
      {
        title: 'Spousal Refusal & Spousal Impoverishment Planning',
        description:
          'When one spouse needs nursing-home Medicaid and the other does not, the spousal impoverishment rules and the right of spousal refusal offer significant protection. We file the spousal refusal letter, calculate the CSRA and MMMNA accurately, and pursue the fair-hearing process where the community spouse needs additional resources to meet the documented standard of living. DSS routinely brings a recovery claim against the refusing spouse; we defend.',
        bullets: [
          'CSRA calculation ($74,820 to $154,140 in 2024)',
          'MMMNA calculation ($3,853.50 in 2024)',
          'Spousal refusal letter preparation',
          'Fair-hearing representation for upward CSRA adjustment',
          'Defense against DSS recovery claims',
        ],
      },
      {
        title: 'Pooled Income Trusts',
        description:
          'For an applicant whose income exceeds the Medicaid limit (~$1,732/month for an individual in 2024 community Medicaid), a pooled income trust operated by a New York nonprofit captures the excess income each month and applies it to the beneficiary’s living expenses. The trust preserves Medicaid eligibility without forcing the applicant to spend down to the income cap. The remainder at death passes to the nonprofit or other Medicaid recipients in the pool.',
        bullets: [
          'NY non-profit pooled trust enrollment',
          'Monthly excess income transfer',
          'Bill-pay coordination for housing, utilities',
          'No lookback applies to pooled trust contributions',
          'Remainder to nonprofit at death',
        ],
      },
      {
        title: 'Long-Term Care Insurance Review',
        description:
          'For clients in their 50s and early 60s, long-term care insurance is a meaningful alternative to a MAPT-based plan. We review existing policies for benefit-period adequacy, inflation protection, elimination-period appropriateness, and home-care coverage. New York’s Partnership for Long-Term Care provides additional dollar-for-dollar resource disregard when a Partnership-qualified policy is in force.',
        bullets: [
          'Existing policy adequacy review',
          'NY Partnership for Long-Term Care analysis',
          'Inflation protection assessment',
          'Home care vs. nursing home benefit balance',
          'Coordination with MAPT-based plan',
        ],
      },
      {
        title: 'Elder Abuse Intervention & GAL Appointments',
        description:
          'When a vulnerable adult is exploited financially or physically, the family’s remedy may include an Article 81 guardianship, a guardian ad litem appointment, a protective order, or coordinated reporting to Adult Protective Services. We intervene quickly to freeze accounts, recover transferred assets, and ensure the elder’s safety.',
        bullets: [
          'APS coordination and reporting',
          'Emergency Article 81 petitions',
          'GAL appointments for accounting proceedings',
          'Recovery of converted assets',
          'Protective orders against bad-actor agents',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Elder Law — Key Facts (2024)',
      intro: 'The lookbacks, dollar thresholds, and statutes that govern every elder law plan.',
      facts: [
        { label: 'Institutional Medicaid lookback', value: '60 months (5 years) under SSL § 366' },
        { label: 'Community Medicaid lookback', value: '30 months — delayed, not enforced as of 2024' },
        { label: 'Community Medicaid income limit (individual)', value: '~$1,732/month (2024)' },
        { label: 'Community Medicaid resource limit (individual)', value: '~$31,175 (2024)' },
        { label: 'Community Spouse Resource Allowance (CSRA)', value: '$74,820 floor / $154,140 ceiling (2024)' },
        { label: 'Minimum Monthly Maintenance Needs Allowance', value: '$3,853.50/month (2024)' },
        { label: 'Pooled income trust', value: 'Must be operated by NY nonprofit' },
        { label: 'Governing statute', value: 'NY Social Services Law § 366' },
      ],
    },
    process: {
      headline: 'Our Elder Law Process',
      intro: 'A defined path from asset audit through approval and ongoing recertification.',
      steps: [
        {
          title: 'Asset & Income Audit',
          description:
            'A complete inventory of countable resources, exempt assets, retirement accounts, pension and Social Security income, recent transfers (looking back 60 months), and the family home. We identify the regional cost of care that will drive any transfer penalty calculation.',
        },
        {
          title: 'Strategy Design',
          description:
            'Based on the audit and the timeline (how soon will care be needed?), we design the appropriate combination of MAPT funding, spousal refusal, spend-down on exempt assets, pooled-income-trust enrollment, and long-term care insurance utilization.',
        },
        {
          title: 'Document Drafting & Trust Funding',
          description:
            'For MAPT-based plans, we draft the irrevocable trust, prepare the deed to retitle the home into the trust, and execute the brokerage and bank account funding letters. Funding is where most MAPTs fail — an unfunded trust does not shelter anything.',
        },
        {
          title: 'Application Filing',
          description:
            'When care becomes imminent, we assemble the application package (five years of bank statements, deed history, transfer documentation, income verification), file with the local DSS, and attend the eligibility interview. Community Medicaid filings typically resolve in 45 to 90 days; institutional filings take longer.',
        },
        {
          title: 'Approval & Recertification',
          description:
            'Once approved, the case enters annual recertification — a reduced-documentation refresh of the application each year. We handle the recertification on a flat fee, and we respond to mid-year inquiries from DSS as they arise.',
        },
      ],
    },
    faq: [
      {
        question: 'What is the 5-year Medicaid lookback?',
        answer:
          "Under SSL § 366, when a New Yorker applies for institutional (nursing home) Medicaid, the agency reviews the 60 months immediately preceding the application. Any uncompensated transfer in that window — a gift to a child, a transfer into a MAPT, a below-market sale of property — creates a transfer penalty. The penalty is calculated by dividing the value of the transferred asset by the regional monthly cost of care to determine the months of ineligibility.",
      },
      {
        question: 'Does community Medicaid have a lookback in New York?',
        answer:
          'New York passed a 30-month lookback for community Medicaid (home care, CDPAP, certified home health) in 2020, but enforcement has been delayed repeatedly through the state budget process. As of 2024, the community lookback is still not being enforced — but this is a moving target, and the enforcement decision can be made each budget cycle. Plan as if it will be enforced, and verify the current enforcement status before any final transfer.',
      },
      {
        question: 'What is a MAPT?',
        answer:
          "A Medicaid Asset Protection Trust is an irrevocable trust into which the client transfers the family home and liquid assets, with the goal of removing those assets from the Medicaid eligibility calculation after the 60-month lookback. The grantor cannot serve as trustee and cannot retain access to principal, but typically retains the right to income generated by the trust assets and (importantly) the right to live in the home. Children commonly serve as trustees, and at the grantor’s death the assets pass to the beneficiaries with a stepped-up basis under IRC § 1014.",
      },
      {
        question: 'Can I keep my house if it is in a MAPT?',
        answer:
          'Yes. A properly drafted MAPT reserves the grantor a life estate or a retained right of occupancy in the family home. The grantor continues to live in the home, pay the property taxes (with the STAR/Enhanced STAR exemption intact in most cases), and treat the home as their residence. The grantor cannot, however, sell the home or borrow against it directly — those decisions belong to the trustee.',
      },
      {
        question: 'What is spousal refusal?',
        answer:
          "When one spouse needs institutional Medicaid and the other does not, the community spouse can formally refuse to contribute the community spouse’s resources or income beyond the CSRA and MMMNA to the institutionalized spouse’s care. New York recognizes spousal refusal, although DSS routinely files a recovery action against the refusing spouse for the cost of care that exceeded the institutionalized spouse’s share. We file the refusal letter and defend the recovery action.",
      },
      {
        question: 'What is the Community Spouse Resource Allowance?',
        answer:
          'The CSRA is the portion of a married couple’s combined countable resources that is sheltered for the community spouse when the other spouse applies for institutional Medicaid. For 2024, the CSRA floor is $74,820 and the ceiling is $154,140, with the actual allocation falling somewhere in that range based on the couple’s combined resources at the snapshot date. A community spouse with documented higher need can apply for an upward adjustment through a fair hearing.',
      },
      {
        question: 'Is income above the Medicaid limit fatal to my application?',
        answer:
          'No. A pooled income trust operated by a New York nonprofit captures the excess income each month and applies it to the beneficiary’s living expenses. The trust preserves Medicaid eligibility without forcing the applicant to spend down to the income cap. There is no lookback on contributions to a pooled income trust — the excess income can be diverted immediately at application without penalty.',
      },
      {
        question: 'How long does Medicaid approval take?',
        answer:
          'Community Medicaid applications typically resolve in 45 to 90 days from filing. Institutional Medicaid applications take longer — three to six months is common, particularly for filings that include MAPT transfers or other complex transactions requiring detailed documentation. The clock is paused each time DSS issues a deficiency notice, so a clean initial filing materially shortens the timeline.',
      },
      {
        question: 'What if I need nursing home care now and have not planned?',
        answer:
          "Crisis planning still has real tools. A spousal refusal letter can immediately protect the community spouse’s resources above the CSRA. Gifts to children combined with promissory notes can convert a portion of the assets into a tolerable transfer penalty. Conversion of countable resources into exempt assets (paying off a mortgage, prepaying a funeral) can reduce countable resources without triggering a transfer penalty. The toolkit is narrower than pre-planning, but it is not empty.",
      },
      {
        question: 'Does an irrevocable trust avoid estate recovery?',
        answer:
          'Generally yes. New York Medicaid estate recovery under federal law targets the deceased Medicaid recipient’s "estate" — defined for New York purposes as the probate estate, not the broader expanded definition used in some states. Assets held in a properly drafted MAPT pass outside probate to the named beneficiaries, beyond the reach of estate recovery. The same is true for assets passing by beneficiary designation, joint title with right of survivorship, or transfer-on-death designations.',
      },
    ],
    cta: {
      headline: 'Plan Your Elder Law Strategy Today',
      body: 'Schedule a free 30-minute consultation with Russel Morgan, Esq. We will run the lookback math, scope the right trust, and explain the timeline — no obligation.',
    },
    related: ['estate-planning', 'trusts', 'guardianship', 'power-of-attorney'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
  'family-law': {
    slug: 'family-law',
    url: '/family-law/',
    title: 'NYC Family Law Attorney | Divorce, Custody, Support | Morgan Legal',
    metaDescription:
      'New York family law — divorce, custody, child support and equitable distribution. Five boroughs and Long Island. Call (888) 529-1315.',
    heroEyebrow: 'Family Law · NY DRL',
    heroHeadline: 'Family Law With',
    heroHeadlineAccent: 'Discretion and Strength',
    heroLead:
      'Morgan Legal Group represents New Yorkers across the full range of contested and uncontested matrimonial and family matters — divorce under DRL § 170, equitable distribution of marital property, custody under the best-interests-of-the-child standard, and child support calculated under the Child Support Standards Act. Discreet counsel, calm execution, and the financial sophistication these cases require.',
    overview: {
      headline: 'Family Law in New York',
      paragraphs: [
        "New York Domestic Relations Law (DRL) is the governing statute for everything that happens when a marriage forms, ends, or is renegotiated. Divorce, custody, child support, spousal maintenance, adoption, paternity, and family offense matters all trace back to specific DRL articles. Family matters can be heard in Supreme Court (which has exclusive jurisdiction over divorce itself) or in Family Court (which handles custody, child support, and family offense petitions outside the divorce context). Choosing the right forum is one of the earliest strategic decisions in every case.",
        "Grounds for divorce live in DRL § 170. New York was the last state in the country to adopt no-fault divorce — the no-fault ground (DRL § 170(7), the irretrievable breakdown of the relationship for at least six months) was enacted in 2010 and now drives the overwhelming majority of New York filings. Fault grounds (cruel and inhuman treatment, abandonment for at least one year, imprisonment for three or more years, adultery, and conversion of a separation judgment) remain available but are rarely invoked when no-fault will accomplish the same outcome with less acrimony and lower cost.",
        "Equitable distribution under DRL § 236(B)(5) is the rule that surprises most New Yorkers. New York is NOT a community property state — there is no automatic 50-50 split of marital assets. Instead, the court divides marital property fairly after weighing fourteen statutory factors, including the duration of the marriage, the age and health of the parties, the income and property each brought to the marriage, the loss of inheritance and pension rights from the dissolution, and the tax consequences to each party. Separate property — assets owned before the marriage, inheritances, and gifts to one spouse alone — is generally NOT subject to distribution, although commingling can convert separate property into marital property.",
        "Child support is calculated under the Child Support Standards Act (DRL § 240(1-b)) using a strict percentage formula: 17% of combined parental income for one child, 25% for two, 29% for three, 31% for four, and no less than 35% for five or more children. For 2024, the formula presumption applies to the first $183,000 of combined parental income; above that cap, the court may apply the percentage or deviate based on the same statutory factors. Custody decisions follow the best-interests-of-the-child standard articulated in Eschbach v Eschbach, with separate determinations made for legal custody (decision-making authority over education, medical care, and religion) and residential custody (where the child primarily lives).",
      ],
    },
    subServices: [
      {
        title: 'Divorce — Contested & Uncontested',
        description:
          'We handle both contested and uncontested New York divorces, with a strong preference for resolution by negotiated settlement or mediation when the facts and personalities permit. An uncontested divorce on the no-fault ground (DRL § 170(7)) with a fully signed separation agreement can resolve in three to nine months; a contested high-conflict divorce can take one to two years or more.',
        bullets: [
          'No-fault grounds (DRL § 170(7))',
          'Fault grounds where strategically appropriate',
          'Negotiated separation agreements',
          'Conversion of separation to divorce after one year',
          'Trial preparation and contested litigation',
        ],
      },
      {
        title: 'Child Custody & Visitation',
        description:
          'New York custody decisions follow the best-interests-of-the-child standard, with separate analysis of legal custody (decision-making authority) and residential custody (primary residence). We litigate initial custody determinations, defend or prosecute modification petitions on a substantial-change-in-circumstances showing, and negotiate detailed parenting plans that anticipate holidays, vacations, education choices, and relocation.',
        bullets: [
          'Best-interests-of-the-child analysis',
          'Legal vs. residential custody distinction',
          'Parenting plan drafting and negotiation',
          'Modification petitions (substantial change in circumstances)',
          'Relocation cases (Tropea v Tropea framework)',
        ],
      },
      {
        title: 'Child Support — CSSA Calculations & Modifications',
        description:
          'Child support in New York is governed by the Child Support Standards Act (DRL § 240(1-b)), which applies a strict percentage of combined parental income (17/25/29/31/35%) up to the 2024 cap of $183,000. We calculate the basic obligation, add-on costs (childcare, health insurance, unreimbursed medical), and arrears, and we litigate modification petitions and enforcement proceedings in Family Court and Supreme Court.',
        bullets: [
          'CSSA percentage formula (17 / 25 / 29 / 31 / 35%)',
          '$183,000 combined income cap for 2024',
          'Add-on costs (childcare, health insurance)',
          'Modification on substantial change in circumstances',
          'Enforcement via income execution and contempt',
        ],
      },
      {
        title: 'Spousal Maintenance (Alimony)',
        description:
          'New York enacted a statutory maintenance formula in 2015 (DRL § 236(B)(6)) that calculates a presumptive temporary and post-divorce maintenance amount based on the parties\' incomes and the duration of the marriage. The formula creates a presumption that the court can override on a record of statutory factors. For 2024, the formula applies to the first $228,000 of the payor\'s income; above that cap, the court has full discretion.',
        bullets: [
          'Statutory formula under DRL § 236(B)(6)',
          '$228,000 payor income cap for 2024',
          'Duration tied to length of marriage (advisory schedule)',
          'Temporary (pendente lite) maintenance during litigation',
          'Modification on substantial change in circumstances',
        ],
      },
      {
        title: 'Equitable Distribution of Marital Property',
        description:
          'Equitable distribution under DRL § 236(B)(5) divides marital property fairly after weighing fourteen statutory factors. We identify and value marital assets (real estate, retirement accounts, business interests, deferred compensation), trace separate property carve-outs (pre-marital assets, inheritances, gifts), negotiate or litigate the division, and prepare the qualified domestic relations orders (QDROs) needed to divide retirement plans.',
        bullets: [
          'Marital vs. separate property classification',
          'Fourteen-factor equitable distribution analysis',
          'Retirement asset division and QDRO drafting',
          'Real estate buy-out and sale negotiations',
          'Tracing of separate property and commingling defenses',
        ],
      },
      {
        title: 'Family Court Petitions',
        description:
          'Family Court has concurrent jurisdiction over child support (Article 4), custody and visitation (Article 6), and family offense petitions (Article 8). It does not handle divorce itself, but it is often the right forum for support and custody disputes between unmarried parents or between parents who have already divorced. We file, defend, and litigate the full range of Family Court petitions across all five boroughs and Long Island.',
        bullets: [
          'Article 4 child support petitions',
          'Article 6 custody and visitation',
          'Article 8 family offense (orders of protection)',
          'Article 10 abuse and neglect defense',
          'Paternity and acknowledgment of paternity',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Family Law — Key Facts',
      intro: 'The statutes, thresholds, and timelines that govern every New York family matter.',
      facts: [
        { label: 'Governing statute', value: 'NY Domestic Relations Law (DRL)' },
        { label: 'No-fault ground enacted', value: '2010 — DRL § 170(7), 6-month breakdown' },
        { label: 'Residency requirement', value: '1 year for most filings (DRL § 230)' },
        { label: 'Equitable distribution', value: 'NOT 50-50 default — 14 statutory factors' },
        { label: 'CSSA percentages', value: '17 / 25 / 29 / 31 / 35% (1-5+ children)' },
        { label: 'CSSA combined income cap (2024)', value: '$183,000' },
        { label: 'Family offense statute of limitations', value: '1 year' },
        { label: 'Court structure', value: 'Supreme Court (divorce) + Family Court (support, custody, family offense)' },
      ],
    },
    process: {
      headline: 'Our Family Law Process',
      intro: 'A defined path from initial consultation to post-judgment enforcement.',
      steps: [
        {
          title: 'Initial Consultation',
          description:
            'A confidential 30-minute consultation to understand the marriage, the assets, the children, and the immediate concerns. We confirm the right forum (Supreme Court vs. Family Court) and identify any temporary relief that needs to be sought at the outset.',
        },
        {
          title: 'Strategy & Financial Discovery',
          description:
            'We assemble the financial picture — tax returns, bank statements, retirement account balances, business interests, real estate equity, and the marital lifestyle. A statement of net worth is prepared in the form required by 22 NYCRR § 202.16(b). Where assets may have been concealed, we engage forensic accountants.',
        },
        {
          title: 'Negotiation & Mediation',
          description:
            'In most New York divorces, the strongest path is a negotiated separation agreement that resolves equitable distribution, custody, support, and maintenance in a single document. We negotiate directly with opposing counsel, attend mediation where appropriate, and convert the resulting agreement into a judgment of divorce on the no-fault ground.',
        },
        {
          title: 'Litigation If Needed',
          description:
            'When negotiation cannot resolve the case — typically when custody is genuinely contested, assets have been concealed, or domestic violence is present — we litigate. Motion practice, depositions, custody evaluations, and trial preparation proceed on the court\'s schedule, with settlement remaining possible at every stage.',
        },
        {
          title: 'Settlement or Trial',
          description:
            'The vast majority of New York divorces resolve by settlement. We negotiate from a position of trial-readiness so that the settlement reflects what the court would likely order. Where trial is necessary, we present the case with the financial expertise and child-focused advocacy required.',
        },
        {
          title: 'Post-Judgment Enforcement',
          description:
            'After judgment, life events trigger modifications and enforcement issues — a payor falls behind on support, a parent seeks to relocate, a maintenance payment becomes inappropriate after a substantial change in circumstances. We file modification petitions, enforce arrears via income execution and contempt, and defend modification petitions brought by the other party.',
        },
      ],
    },
    faq: [
      {
        question: 'How long does a New York divorce take?',
        answer:
          'An uncontested no-fault divorce with a fully signed separation agreement typically takes three to nine months from filing to judgment. A contested divorce — particularly one involving custody disputes, complex assets, or concealed income — can take one to two years or more. The single largest accelerant is reaching a comprehensive negotiated settlement; the single largest delay is contested custody.',
      },
      {
        question: 'What is no-fault divorce in New York?',
        answer:
          'No-fault divorce was enacted in New York in 2010 (DRL § 170(7)) and is now the dominant ground for divorce filings. The petitioning spouse must allege under oath that the marriage has been irretrievably broken for a period of at least six months. The other spouse cannot defeat the ground by denying it. Financial and custody issues must still be resolved before judgment, but the ground itself is no longer contested.',
      },
      {
        question: 'How is property divided in a New York divorce?',
        answer:
          'New York is an equitable distribution state, not a community property state. Under DRL § 236(B)(5), the court divides marital property fairly — not necessarily equally — after weighing fourteen statutory factors. Separate property (pre-marital assets, inheritances, and gifts to one spouse alone) is generally not subject to distribution, though commingling can convert separate property into marital property.',
      },
      {
        question: 'How is child support calculated in New York?',
        answer:
          'Under the Child Support Standards Act (DRL § 240(1-b)), the basic child support obligation is calculated as a percentage of combined parental income: 17% for one child, 25% for two, 29% for three, 31% for four, and no less than 35% for five or more children. For 2024, the formula presumption applies to the first $183,000 of combined parental income; above that cap, the court may apply the percentage or deviate based on statutory factors.',
      },
      {
        question: 'What is the difference between legal and residential custody?',
        answer:
          'Legal custody is decision-making authority over major issues — education, medical care, religion. Residential custody is the determination of where the child primarily lives. The two are evaluated separately and frequently allocated differently — a common arrangement is joint legal custody (both parents make major decisions together) with primary residential custody to one parent and structured visitation to the other.',
      },
      {
        question: 'Do I have to live separately from my spouse to file for divorce?',
        answer:
          'No. The 2010 no-fault ground (DRL § 170(7)) does not require physical separation — only an allegation under oath that the marriage has been irretrievably broken for at least six months. Many spouses continue to live in the same home while a divorce is pending, particularly where economic or childcare considerations make separate residences impractical.',
      },
      {
        question: 'Can custody be modified after judgment?',
        answer:
          'Yes, on a showing of substantial change in circumstances since the prior order. Common triggers include a parent\'s relocation, a parent\'s remarriage to an unsuitable partner, a change in the child\'s needs, or a parent\'s failure to comply with the existing order. The standard remains the best interests of the child — modification is granted only where the change in circumstances warrants a different custodial arrangement.',
      },
      {
        question: 'What is a separation agreement?',
        answer:
          'A separation agreement is a contract between spouses that resolves equitable distribution, custody, support, and maintenance issues without immediate divorce. Properly drafted, it can later be incorporated (but not merged) into a judgment of divorce, surviving as a contract enforceable on its own terms. It is the foundational document of an uncontested divorce and the typical roadmap of a negotiated divorce.',
      },
      {
        question: 'Is mediation required in a New York divorce?',
        answer:
          'No, mediation is not required in New York divorce cases. It is encouraged where the parties retain enough trust and communication to negotiate productively. Mediation is generally inappropriate where domestic violence is present, where one spouse has substantially more financial sophistication than the other, or where one spouse is suspected of concealing assets. Our practice is to mediate when productive and litigate when necessary.',
      },
      {
        question: 'How much does a New York divorce cost?',
        answer:
          'Costs vary widely based on whether the divorce is contested or uncontested, the complexity of the financial picture, and the level of conflict over custody. An uncontested no-fault divorce with a clean separation agreement is one of our most economical engagements. Contested divorces are billed hourly with transparent monthly invoicing. We provide a written engagement letter at the outset so there are no surprises.',
      },
    ],
    cta: {
      headline: 'Discuss Your New York Family Matter',
      body: 'Schedule a confidential 30-minute consultation with Russel Morgan, Esq. We will listen, identify the issues, and outline the strategic options — no obligation, no pressure.',
    },
    related: ['matrimonial', 'estate-planning', 'asset-protection'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
  matrimonial: {
    slug: 'matrimonial',
    url: '/matrimonial/',
    title: 'NYC Matrimonial Attorney | Divorce & Prenups | Morgan Legal',
    metaDescription:
      'High-asset matrimonial work — prenuptial agreements (DRL § 236(B)(3)), complex equitable distribution, business valuation, post-divorce modifications. (888) 529-1315.',
    heroEyebrow: 'Matrimonial · Complex Estate & Asset Cases',
    heroHeadline: 'Strategic Matrimonial Counsel for',
    heroHeadlineAccent: 'Complex Estates',
    heroLead:
      'Morgan Legal Group handles the matrimonial cases that require more than family-court reflexes — high-net-worth divorces with closely held business interests, prenuptial agreement drafting and litigation under DRL § 236(B)(3), business valuation disputes, forensic discovery of concealed assets, and the post-divorce enforcement work that protects what was won at settlement or trial.',
    overview: {
      headline: 'When Matrimonial Cases Become Complex',
      paragraphs: [
        "Matrimonial practice and general family law overlap, but they are not the same discipline. Family law covers the human and child-welfare side of relationships — custody, visitation, support, family offense. Matrimonial work focuses on the financial and contractual dimensions of marriage formation, dissolution, and modification: prenuptial and postnuptial agreements, equitable distribution of complex marital estates, business valuation disputes, and the high-stakes negotiation of separation agreements that often involve eight-figure marital estates, family businesses, professional licenses, deferred compensation, and family wealth that traces back generations.",
        "Prenuptial agreements are governed by DRL § 236(B)(3) and are enforceable in New York if they meet both substantive and procedural fairness standards. Substantively, the agreement must not be unconscionable when made — a wildly one-sided allocation can be set aside even decades later. Procedurally, the agreement must be in writing, signed by both parties, and acknowledged with the formality of a deed (notarized acknowledgment in the form prescribed by Real Property Law § 309-a). Full financial disclosure at the time of signing, independent counsel for each party, and adequate time before the wedding are not strictly required by statute but are the strongest defenses against later attack. Postnuptial agreements are permitted, but they carry a rebuttable presumption of overreaching that requires heightened proof of fairness.",
        "Business valuation is the single most contested issue in high-asset matrimonial work. Under principles developed in O'Brien v O'Brien and its progeny, the enterprise goodwill of a closely held business is marital property subject to equitable distribution; personal goodwill attributable specifically to the owner-spouse's individual skill and reputation generally is not. Forensic accountants and valuation experts apply standards such as fair market value, the discount for lack of marketability for closely held interests, and normalization of owner compensation. The choice of valuation expert and the date of valuation can swing the resulting distribution by seven or eight figures.",
        "High-asset equitable distribution turns heavily on the tracing of separate property and the defense against commingling claims. Under DRL § 236(B)(1)(d), separate property includes assets owned before the marriage, inheritances received during the marriage by one spouse alone, gifts received by one spouse alone (other than from the other spouse), and the appreciation in separate property that is not attributable to the active efforts of either spouse during the marriage. The party claiming separate property bears the burden of proof — without contemporaneous records, separate property can drift into the marital pot through commingling, deposit into joint accounts, or active management by the non-titled spouse.",
      ],
    },
    subServices: [
      {
        title: 'Prenuptial Agreements',
        description:
          'We draft, negotiate, and execute prenuptial agreements under DRL § 236(B)(3) that protect premarital wealth, family businesses, professional practices, and anticipated inheritances. The agreement is built around the parties\' actual situation — full financial disclosure attached as schedules, independent counsel for each party, deed-grade acknowledgment, and adequate time before the wedding. We include sunset clauses, sliding-scale distribution mechanics, and forum-selection provisions where appropriate.',
        bullets: [
          'DRL § 236(B)(3) compliance and acknowledgment',
          'Full financial disclosure as exhibit schedules',
          'Independent counsel for each party',
          'Sunset clauses and sliding-scale distribution',
          'Forum-selection and choice-of-law provisions',
        ],
      },
      {
        title: 'Post-Nuptial Agreements',
        description:
          'Postnuptial agreements are permitted in New York but carry a rebuttable presumption of overreaching that requires heightened evidence of fairness. We use them to resolve specific issues that have arisen during a marriage — an inheritance one spouse has received, a business interest one spouse has acquired, a settlement after marital reconciliation — while building the contemporaneous record needed to defend the agreement against later attack.',
        bullets: [
          'Rebuttable presumption of overreaching',
          'Heightened review for fairness',
          'Use in reconciliation after separation',
          'Allocation of newly received inheritances',
          'Business-interest carve-outs during marriage',
        ],
      },
      {
        title: 'High-Asset Divorce',
        description:
          'Eight-figure and larger marital estates require a different toolkit than a typical divorce. We coordinate forensic accountants, valuation experts, tax counsel, and (where appropriate) private investigators. We litigate concealment claims, defend separate property tracings, negotiate complex tax-efficient settlement structures, and prepare the QDROs and deeds needed to implement the resulting allocation. Discretion at every stage is built into the engagement.',
        bullets: [
          'Forensic accountant and valuation expert coordination',
          'Tax-efficient settlement structuring',
          'Family business buy-out negotiations',
          'Concealment and tracing litigation',
          'Discretion and confidentiality protocols',
        ],
      },
      {
        title: 'Business Valuation Disputes',
        description:
          'In closely held business cases, the valuation question often dominates the distribution. We engage qualified valuation experts (CPA / ABV credentials), litigate the choice of valuation date, contest the discount for lack of marketability, normalize the owner-spouse\'s compensation, and isolate enterprise goodwill (marital) from personal goodwill (separate). The marginal hourly cost of the right valuation expert is repaid many times over in the resulting distribution.',
        bullets: [
          'CPA / ABV expert engagement',
          'Enterprise vs. personal goodwill analysis',
          'Discount for lack of marketability litigation',
          'Owner compensation normalization',
          'Valuation date and updating analyses',
        ],
      },
      {
        title: 'Hidden Asset Discovery',
        description:
          'Where one spouse controls the family finances and the other has reason to suspect that assets have been concealed or diverted, forensic financial discovery is the work that levels the playing field. We subpoena bank records, brokerage statements, business records, and tax returns; conduct lifestyle audits to identify spending that exceeds reported income; and engage forensic accountants to trace funds through shell entities and family member accounts.',
        bullets: [
          'Bank, brokerage, and tax-return subpoenas',
          'Lifestyle audits and reverse-engineering',
          'Tracing through shell entities and family accounts',
          'Forensic accountant engagement',
          'Sanctions motions for non-disclosure',
        ],
      },
      {
        title: 'Post-Divorce Modifications & Enforcement',
        description:
          'After judgment, life changes trigger modification and enforcement issues. We file modification petitions on a substantial-change-in-circumstances showing, defend modification petitions brought by the other party, enforce maintenance and support arrears through income execution and contempt proceedings, and litigate the enforcement of property settlement provisions that did not implement cleanly at the time of judgment.',
        bullets: [
          'Modification on substantial change in circumstances',
          'Enforcement via income execution and contempt',
          'Property settlement enforcement actions',
          'QDRO reformation and correction',
          'Cohabitation termination of maintenance',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Matrimonial Law — Key Facts',
      intro: 'The statutes, doctrines, and 2024 thresholds that govern complex matrimonial work.',
      facts: [
        { label: 'Prenup statute', value: 'DRL § 236(B)(3) — writing + deed-grade acknowledgment' },
        { label: 'Marital fault in equitable distribution', value: 'NOT considered (except egregious conduct)' },
        { label: 'Business enterprise goodwill', value: 'Marital property under O\'Brien' },
        { label: 'Business personal goodwill', value: 'Generally separate property' },
        { label: 'Professional licenses (post-2016)', value: 'No longer divisible as separate marital asset' },
        { label: 'Separate property carve-outs', value: 'DRL § 236(B)(1)(d) — pre-marital, inheritance, gifts' },
        { label: 'Maintenance formula', value: 'DRL § 236(B)(6) — enacted 2015 / 2016 calculation' },
        { label: 'Maintenance cap (2024)', value: '$228,000 of payor\'s income' },
      ],
    },
    process: {
      headline: 'Our Matrimonial Process',
      intro: 'A defined path from pre-engagement planning through post-judgment enforcement.',
      steps: [
        {
          title: 'Pre-Engagement Asset Audit',
          description:
            'For prenup work, we begin with a complete inventory of premarital assets, anticipated inheritances, business interests, professional practice equity, and deferred compensation. The schedules attached to the prenup are built from this inventory, and they later serve as the contemporaneous record that defends the agreement against attack.',
        },
        {
          title: 'Strategic Discovery',
          description:
            'For divorce work, we issue document demands, depose the other party, and subpoena third-party records. Where concealment is suspected, we engage forensic accountants and conduct lifestyle audits. The early discovery posture often determines the strength of the settlement position later.',
        },
        {
          title: 'Valuation & Forensic Engagement',
          description:
            'Closely held business interests, professional practices, and concentrated equity positions are valued by qualified independent experts. We litigate the choice of expert, the valuation date, the discount for lack of marketability, and the allocation of enterprise versus personal goodwill. The valuation case is built methodically over months.',
        },
        {
          title: 'Negotiation With Full Disclosure',
          description:
            'Settlement negotiations in high-asset matrimonial cases require both sides to operate from a shared financial record. We exchange full disclosure (statement of net worth, supporting documents, valuation reports), build a settlement matrix, and negotiate from a position of trial-readiness. The vast majority of cases settle once both sides see what trial would actually produce.',
        },
        {
          title: 'Settlement or Trial',
          description:
            'A negotiated settlement embodied in a separation agreement is converted to a judgment of divorce on the no-fault ground. Where settlement cannot be reached — typically because of a fundamental valuation dispute or a custody impasse — we prepare and try the case, with the financial sophistication and child-focused advocacy required.',
        },
        {
          title: 'Post-Judgment Enforcement',
          description:
            'After judgment, we monitor implementation — that retirement plans actually divide under the QDRO, that real estate buy-outs close on schedule, that maintenance payments arrive on time, and that any agreed structured payments perform as drafted. Where implementation breaks down, we file enforcement motions promptly.',
        },
      ],
    },
    faq: [
      {
        question: 'Are prenuptial agreements enforceable in New York?',
        answer:
          'Yes, provided they meet both procedural and substantive standards under DRL § 236(B)(3). Procedurally, the agreement must be in writing, signed by both parties, and acknowledged with the formality of a deed (Real Property Law § 309-a). Substantively, it must not be unconscionable at the time of execution. Full financial disclosure attached as schedules, independent counsel for each party, and adequate time before the wedding are not strictly required by statute but are the strongest defenses against later attack.',
      },
      {
        question: 'Can I draft my own prenup?',
        answer:
          'Technically yes, but the failure rate is high. DIY prenups regularly omit the deed-grade acknowledgment required by DRL § 236(B)(3), include unconscionable terms that a court will not enforce, fail to attach the financial disclosure schedules needed to defend the agreement against later attack, or are signed too close to the wedding to survive a duress challenge. The marginal cost of competent counsel is repaid many times over if the agreement is ever tested.',
      },
      {
        question: 'How is a business valued in a New York divorce?',
        answer:
          'Closely held businesses are valued by qualified independent experts (typically CPAs with ABV credentials) using accepted methodologies — income approach (discounted cash flow), market approach (comparable transactions), or asset approach. The discount for lack of marketability is litigated case by case. The owner-spouse\'s compensation is often normalized to a market rate to isolate the business\'s actual earning power. Choice of expert and choice of valuation date can swing the resulting distribution dramatically.',
      },
      {
        question: 'What is the O\'Brien rule?',
        answer:
          'O\'Brien v O\'Brien is the foundational New York case on the treatment of professional credentials and business goodwill in equitable distribution. The doctrine that survives in current practice is that the enterprise goodwill of a closely held business (the goodwill that would transfer to a buyer) is marital property subject to distribution, while the personal goodwill attributable specifically to the owner-spouse\'s individual reputation and skill is not. The line between the two is the central question in many business-valuation disputes.',
      },
      {
        question: 'Is a professional practice divisible in New York?',
        answer:
          'The practice itself, as a going concern with enterprise value, is divisible to the extent of its enterprise goodwill. The cash flow generated by the practice is reachable through maintenance and child support obligations. The personal professional credentials (the license itself) are no longer divisible as a standalone marital asset under post-2016 law. The combined effect for most professionals is that the business\'s enterprise value is part of equitable distribution, but the practitioner\'s continued earning capacity is captured through support obligations rather than asset division.',
      },
      {
        question: 'How do I prove hidden assets?',
        answer:
          'Through forensic financial discovery — subpoenas of bank, brokerage, and tax records; depositions under oath; lifestyle audits that compare reported income to actual spending; and tracing analysis through shell entities and family member accounts. Where the record supports it, we file sanctions motions for non-disclosure. The early and aggressive discovery posture is often the single best predictor of recovering concealed assets.',
      },
      {
        question: 'Can spousal maintenance be modified after divorce?',
        answer:
          'Yes, on a showing of substantial change in circumstances since the prior order. Common modification triggers include a significant change in either party\'s income, the payee\'s remarriage (which terminates maintenance), the payee\'s cohabitation in a marriage-like relationship (which may terminate maintenance under the parties\' agreement), the payor\'s involuntary loss of employment, or the payor\'s retirement at a reasonable age. The presumptive duration in the original order can also be revisited.',
      },
      {
        question: 'Are inheritances divisible in a New York divorce?',
        answer:
          'Generally no — under DRL § 236(B)(1)(d), an inheritance received by one spouse alone during the marriage is separate property and not subject to equitable distribution. The protection can be lost through commingling: depositing inherited funds into a joint account, using inherited funds to acquire jointly titled property, or actively managing the inheritance jointly during the marriage. The party claiming separate property treatment bears the burden of tracing the asset back to the inheritance.',
      },
      {
        question: 'What is the New York maintenance formula?',
        answer:
          'DRL § 236(B)(6) sets a statutory formula for both temporary (pendente lite) and post-divorce maintenance. The calculation involves the parties\' incomes, whether child support is also being paid, and (for duration) the length of the marriage on an advisory schedule. For 2024, the formula applies to the first $228,000 of the payor\'s income; above that cap, the court has full discretion to apply the formula, deviate, or decline maintenance entirely based on the statutory factors.',
      },
      {
        question: 'Is marital fault considered in New York equitable distribution?',
        answer:
          'Generally no. New York courts since Blickstein v Blickstein and its progeny have held that ordinary marital fault — adultery, irreconcilable differences, garden-variety bad behavior — is not a factor in equitable distribution. The narrow exception is for egregious conduct that shocks the conscience of the court, such as severe domestic violence, attempted murder, or financial conduct that intentionally dissipated marital assets. In ordinary divorces, the equitable distribution analysis is financial, not moral.',
      },
    ],
    cta: {
      headline: 'Discreet Counsel for Complex Matrimonial Matters',
      body: 'Schedule a confidential 30-minute consultation with Russel Morgan, Esq. We will assess the case, identify the strategic and financial issues, and quote a transparent engagement — with the discretion these cases require.',
    },
    related: ['family-law', 'estate-planning', 'asset-protection', 'trusts'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
  'real-estate': {
    slug: 'real-estate',
    url: '/real-estate/',
    title: 'NYC Real Estate Attorney | Co-ops, Condos, Closings | Morgan Legal',
    metaDescription:
      'New York City real estate attorneys — co-op & condo purchases, closings, deed transfers, life estates, commercial leases. RPL & RPAPL compliant. (888) 529-1315.',
    heroEyebrow: 'Real Estate · NY RPL & RPAPL',
    heroHeadline: 'New York Real Estate',
    heroHeadlineAccent: 'Without the Drama',
    heroLead:
      'Morgan Legal Group represents buyers, sellers, sponsors, and landlords on residential and commercial real estate matters across New York City and the surrounding counties. We work inside the two-attorney closing custom, navigate co-op board approval, draft and negotiate the contract rider, run title and lien diligence, and close on the schedule the deal actually requires.',
    overview: {
      headline: 'Real Estate Law in New York',
      paragraphs: [
        'New York is a two-attorney closing state by long-standing custom — buyer and seller each retain counsel, the title company runs the closing in an attorney-supervised conference, and the lender (where there is one) participates through its own counsel. The custom is not statutory, but in practice every meaningful NYC transaction proceeds this way. Going without counsel exposes the unrepresented party to a contract written entirely for the other side and to closing-table surprises that competent counsel would have caught a month earlier.',
        'The governing statutes are the New York Real Property Law (RPL) for contracts, deeds, and conveyance mechanics; the Real Property Actions and Proceedings Law (RPAPL) for foreclosure, partition, and possession litigation; and Article 14 of the Business Corporation Law (BCL) for cooperative housing corporations. Each layer of statute matters to a different stage of the transaction — RPL governs how the deed must be drafted and acknowledged, RPAPL governs what happens when the deal goes wrong, and BCL Article 14 governs the rights of co-op shareholders and the proprietary lease.',
        'The NYC distinction between co-ops and condos is fundamental. A co-op buyer acquires shares in a cooperative corporation plus a proprietary lease to a specific apartment — board approval is required, financial scrutiny is heavy, and the recognition agreement between lender, buyer, and the co-op governs the lender\'s rights. A condo buyer acquires fee title to a unit plus an undivided interest in common elements — the offering plan and bylaws control, board approval is generally limited to a right of first refusal, and the unit deed is recorded with the City Register.',
        'On the tax and litigation side, New York imposes a state transfer tax on every conveyance, NYC layers its own Real Property Transfer Tax (RPTT) on top, and the mansion tax applies to residential transactions over $1 million on a graduated schedule that escalates through the high seven figures. Section 1031 like-kind exchanges remain available for investment property. The lis pendens recorded on a real estate dispute is itself a powerful litigation tool that can paralyze a transaction until the underlying claim is resolved.',
      ],
    },
    subServices: [
      {
        title: 'Residential Purchase & Sale',
        description:
          'We represent buyers and sellers from contract through closing on co-op, condo, townhouse, and single-family transactions. The work centers on the contract rider — financing and mortgage contingencies, due-diligence carve-outs, time-of-the-essence mechanics, default provisions, and the allocation of transfer taxes — and on closing the title and survey diligence before the contingency periods expire.',
        bullets: [
          'Contract review and attorney rider',
          'Financing and mortgage contingencies',
          'Title and survey diligence',
          'Closing statement preparation',
          'Recordation and post-closing follow-up',
        ],
      },
      {
        title: 'Co-op Closings',
        description:
          'Co-op transactions add a layer of board scrutiny that the standard residential closing never sees. We assemble and review the board package — financial statements, reference letters, tax returns, employment verifications — coordinate the board interview, and negotiate the lender recognition agreement (Aztech form is the New York standard) that makes the financing work inside the cooperative\'s consent structure.',
        bullets: [
          'Board package assembly and review',
          'Financial statement preparation guidance',
          'Recognition agreement negotiation',
          'Stock and proprietary lease transfer',
          'Co-op flip tax allocation',
        ],
      },
      {
        title: 'Condo Closings',
        description:
          'Condominium transactions turn on the offering plan, the bylaws, the declaration, and the unit deed. We review the offering plan and the most recent financial statements, calculate common charges adjustment at closing, confirm the unit\'s status free of liens and judgments, and record the deed and any mortgage with the City Register through ACRIS.',
        bullets: [
          'Offering plan and bylaws review',
          'Common charges and assessment adjustment',
          'Unit deed drafting and acknowledgment',
          'ACRIS recordation and transfer tax filing',
          'Right of first refusal waiver',
        ],
      },
      {
        title: 'Commercial Real Estate',
        description:
          'Commercial work runs from net leases and retail leases to ground leases and build-out riders. We negotiate the substantive economics (rent, escalations, operating expenses, real estate tax pass-throughs, work letters) and the legal structure (assignment and subletting, default and cure, SNDA agreements with the landlord\'s lender, estoppel certificates) that determines what happens when the deal is tested.',
        bullets: [
          'Net and retail lease negotiation',
          'Build-out riders and work letters',
          'SNDA and estoppel certificate review',
          'Ground lease structuring',
          'Assignment and subletting provisions',
        ],
      },
      {
        title: 'Deed Transfers & Life Estates',
        description:
          'We draft and record deeds under RPL § 240 for intra-family transfers, transfers to revocable trusts, and life-estate retained-life-interest deeds used in elder-law and Medicaid planning. New York does not recognize beneficiary deeds or transfer-on-death deeds for real estate — the planning has to be done through a deed delivered during life or through a trust, never through a TOD instrument.',
        bullets: [
          'Intra-family deed transfers',
          'Trust funding deeds (revocable and irrevocable)',
          'Retained life estate deeds (Medicaid-compatible)',
          'No NY TOD/POD deeds — alternative planning required',
          'ACRIS recordation and transfer tax exemption claims',
        ],
      },
      {
        title: 'Real Estate Litigation',
        description:
          'When the transaction breaks down or the title turns out to be defective, we litigate. We file partition actions under RPAPL Article 6 to force the sale of jointly held property when co-owners cannot agree, record and defend lis pendens notices, prosecute and defend breach-of-contract claims arising from real estate deals, and litigate easement, boundary, and adverse possession disputes.',
        bullets: [
          'RPAPL Article 6 partition actions',
          'Lis pendens prosecution and defense',
          'Breach of contract and specific performance',
          'Easement, boundary, and title disputes',
          'Adverse possession claims',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Real Estate — Key Facts',
      intro: 'The statutes, tax rates, and customary practice that govern New York real estate.',
      facts: [
        { label: 'Two-attorney closing', value: 'Customary, not statutory — universal in NYC' },
        { label: 'NYC RPTT (residential)', value: '1.0% under $500K · 1.425% $500K–$1M · 1.825% over $1M' },
        { label: 'NYC RPTT (commercial)', value: '1.425% under $500K · 2.625% over $500K' },
        { label: 'NY State transfer tax', value: '$2 per $500 of consideration ($4/$1,000)' },
        { label: 'NY mansion tax', value: '1% over $1M, graduated up to 3.9% over $25M (NYC)' },
        { label: 'NYC closing down-payment custom', value: '10% at contract, balance at closing' },
        { label: 'Co-op flip tax (typical)', value: '1%–3% of sale price (older buildings)' },
        { label: 'SOL on real estate contract', value: '6 years under CPLR § 213' },
        { label: 'Mechanic\'s lien deadline', value: '8 months residential / 4 months commercial (Lien Law § 10)' },
      ],
    },
    process: {
      headline: 'Our Real Estate Closing Process',
      intro: 'A defined path from contract through closing and post-closing recordation.',
      steps: [
        {
          title: 'Engagement & Initial Review',
          description:
            'We open the engagement, request the listing materials, financing pre-approval, and (for co-ops/condos) the offering plan and most recent financial statements. We confirm the parties, the deal structure, and the closing target date — and identify the contract terms that will require the most negotiation.',
        },
        {
          title: 'Contract Review & Negotiation',
          description:
            'We review the seller-drafted contract and prepare an attorney rider that addresses financing contingencies, mortgage contingency dates, default mechanics, title objections, transfer tax allocation, and (where relevant) tenancy and possession issues. We negotiate the rider with seller\'s counsel until both sides sign.',
        },
        {
          title: 'Title Search & Survey',
          description:
            'We order the title report and survey, review for liens, judgments, easements, encroachments, and chain-of-title issues, and prepare title objection letters where needed. Open mortgages, tax arrears, and Department of Finance violations are flagged for cure before closing.',
        },
        {
          title: 'Due Diligence',
          description:
            'For co-ops, we assemble and review the board package and coordinate the board interview. For condos, we review the offering plan and bylaws and confirm the right of first refusal waiver. For commercial, we review the rent roll, estoppel certificates, and SNDA agreements. Inspection contingencies are released or extended in this window.',
        },
        {
          title: 'Closing & Funding',
          description:
            'At closing, we review the title company\'s closing statement, confirm the wire transfers, attend the closing conference with the buyer (or seller) and the title closer, sign the deed (or accept the deed and stock-and-lease for co-ops), and disburse the proceeds. The keys change hands when the funds clear.',
        },
        {
          title: 'Post-Closing Recordation',
          description:
            'The title company records the deed and any new mortgage with the City Register (ACRIS for NYC) and files the RPTT and NYS transfer tax returns. We confirm recordation, deliver the recorded deed to the buyer, and close the file once all post-closing items (refunds of escrow, return of pro-rated charges, etc.) are resolved.',
        },
      ],
    },
    faq: [
      {
        question: 'Do I need an attorney to buy real estate in NYC?',
        answer:
          'Technically no — there is no statute requiring buyer\'s counsel. Practically, yes. Every co-op and condo board, every lender, every title company, and every seller will expect you to have counsel. The contract is drafted by seller\'s attorney and is written entirely for the seller until your counsel negotiates the rider. Going without counsel on a six- or seven-figure transaction is a false economy.',
      },
      {
        question: 'What is a co-op board package?',
        answer:
          'The board package is the application a prospective co-op buyer submits to the cooperative\'s board of directors before purchase. It typically includes a personal financial statement, two to three years of tax returns and W-2s, employment verification, two to four reference letters (personal and professional), the executed contract of sale, the mortgage commitment letter, the buyer\'s background acknowledgment, and the board interview that follows submission. Co-op boards have substantial discretion to approve or deny without giving reasons (subject only to fair housing law).',
      },
      {
        question: 'How long does a NYC closing take?',
        answer:
          'Sixty to ninety days from signed contract to closing is typical for financed residential transactions. The pacing is driven by the mortgage commitment date (usually thirty to forty-five days after contract), the title and survey work, and — for co-ops — the board package, interview, and approval letter. Cash deals can close in three to four weeks. Commercial transactions often run ninety to one hundred eighty days because of the diligence and lender involvement.',
      },
      {
        question: 'What is the mansion tax?',
        answer:
          'The mansion tax is a New York State transfer tax imposed on residential real estate sold for $1 million or more. It is the buyer\'s obligation by default (the contract can shift it). For NYC properties, the tax is graduated: 1% from $1M to $2M, 1.25% from $2M to $3M, 1.5% from $3M to $5M, 2.25% from $5M to $10M, 3.25% from $10M to $15M, 3.5% from $15M to $20M, 3.75% from $20M to $25M, and 3.9% over $25M. The tax is paid at closing through the title company.',
      },
      {
        question: 'Who pays the NYC transfer tax?',
        answer:
          'By default, the seller pays both the NYC Real Property Transfer Tax (RPTT) and the New York State transfer tax. New construction sponsor sales typically shift the RPTT to the buyer through a sponsor rider. Commercial transactions sometimes split the tax by negotiation. The mansion tax (a separate state imposition over $1M residential) is the buyer\'s obligation by default.',
      },
      {
        question: 'What is title insurance?',
        answer:
          'Title insurance is an indemnity policy that protects the insured (lender, owner, or both) against losses from defects in title that existed at the time of issuance — undisclosed liens, chain-of-title errors, forged deeds, missing heirs, recording errors. Owner\'s policy is one-time premium based on purchase price; lender\'s policy is based on loan amount. Both are issued at closing. Most NYC closings include both.',
      },
      {
        question: 'Can I assign a real estate contract?',
        answer:
          'Generally yes for residential transactions if the contract is silent or expressly permits assignment, but in practice almost every NYC residential contract prohibits assignment without seller consent. Commercial contracts often expressly permit assignment to affiliates and prohibit it to third parties. The mechanics matter: an assignment requires the assignee to step into the assignor\'s obligations, transfer tax may be due twice (once on the assignment and once on the closing), and the original deposit may need to be replaced.',
      },
      {
        question: 'What is a 1031 like-kind exchange?',
        answer:
          'Section 1031 of the Internal Revenue Code permits a taxpayer to defer capital gains tax on the sale of investment or business real estate by reinvesting the proceeds in like-kind property within strict statutory deadlines — forty-five days to identify the replacement property and one hundred eighty days to close on it. A qualified intermediary holds the proceeds between sale and reinvestment to satisfy the constructive-receipt rule. Primary residences are not eligible.',
      },
      {
        question: 'Is a beneficiary deed valid in New York?',
        answer:
          'No. New York does not recognize beneficiary deeds, transfer-on-death deeds, or TOD/POD instruments for real estate. Real property in New York must be transferred during life by a recorded deed delivered to the grantee, or at death through the will and probate (or through a trust that holds title before death). The states that recognize TOD deeds (Arizona, Colorado, Missouri, and others) do not provide a model that works here — the planning has to be done through a lifetime deed or a trust.',
      },
      {
        question: 'What is a lis pendens?',
        answer:
          'A lis pendens (Latin: "suit pending") is a notice recorded against title to real property that alerts the world to a pending lawsuit affecting that property. In New York, the notice is authorized by CPLR Article 65 and is the standard procedural tool in specific performance actions, partition actions under RPAPL Article 6, foreclosure proceedings, and any other litigation seeking title-affecting relief. A recorded lis pendens makes the property effectively unmarketable until the underlying litigation resolves or the notice is canceled.',
      },
    ],
    cta: {
      headline: 'Buy or Sell New York Real Estate With Counsel',
      body: 'Schedule a 30-minute consultation with Morgan Legal Group. We will review the contract, identify the negotiation points, and quote a flat fee for the closing — with the discretion and responsiveness New York real estate requires.',
    },
    related: ['estate-planning', 'asset-protection', 'trusts'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
  'personal-injury': {
    slug: 'personal-injury',
    url: '/personal-injury/',
    title: 'NYC Personal Injury Attorney | Auto & Slip and Fall | Morgan Legal',
    metaDescription:
      'New York personal injury attorneys — motor vehicle accidents (no-fault under Ins Law § 5102), slip & fall, premises liability, medical malpractice. (888) 529-1315.',
    heroEyebrow: 'Personal Injury · NY Insurance Law § 5102',
    heroHeadline: 'Personal Injury Counsel Built for',
    heroHeadlineAccent: 'Real Recovery',
    heroLead:
      'Morgan Legal Group represents injured New Yorkers on a contingency-fee basis — no recovery, no fee. We handle motor vehicle accidents inside the no-fault scheme, slip and fall and premises liability matters, medical malpractice, construction Labor Law cases, and wrongful death actions. Investigation begins the day we are retained, because evidence in injury cases disappears fast.',
    overview: {
      headline: 'Personal Injury in New York',
      paragraphs: [
        'New York operates a no-fault auto insurance system under Insurance Law § 5102. After an automobile accident, your own carrier pays the first $50,000 of medical bills and lost wages regardless of who was at fault — Personal Injury Protection (PIP) benefits. To sue the other driver beyond no-fault PIP, the injured party must establish a "serious injury" under the § 5102(d) threshold (significant disfigurement, fracture, permanent loss or significant limitation of a body function, or 90/180-day disability). The serious injury threshold is the central battleground in most NYC auto cases.',
        'New York is a pure comparative fault jurisdiction under CPLR § 1411. Recovery is reduced by the plaintiff\'s percentage of fault, but it is never barred — a plaintiff who is 70% at fault still recovers 30% of the damages, and a plaintiff who is 99% at fault still recovers 1%. This is one of the most plaintiff-favorable comparative-fault rules in the country, and it is one of the most important reasons New York remains a viable forum for catastrophic-injury litigation.',
        'Statutes of limitations are short and absolutely fatal if missed. General negligence: three years under CPLR § 214. Medical malpractice: two and a half years under CPLR § 214-a from the act or the end of continuous treatment. Wrongful death: two years from death under EPTL § 5-4.1. Claims against the City of New York, NYC Transit, the MTA, or any other municipal defendant require a Notice of Claim within ninety days of the incident and the action commenced within one year and ninety days, per General Municipal Law §§ 50-e and 50-i.',
        'Premises liability in New York is governed by a notice-based negligence framework. The property owner is liable only if the owner had actual or constructive notice of the dangerous condition and a reasonable opportunity to remedy it. "Open and obvious" hazards may negate the duty to warn (though not necessarily the duty to maintain). Whether the condition was "transient" or "recurrent" often determines whether constructive notice can be established. The case is won or lost on the documentary record built in discovery.',
      ],
    },
    subServices: [
      {
        title: 'Motor Vehicle Accidents',
        description:
          'We handle car, truck, motorcycle, pedestrian, and bicycle accident cases inside the no-fault scheme. Work begins with PIP application to the host carrier, then proceeds to the third-party negligence claim against the other driver — establishing the § 5102(d) serious injury threshold, calculating economic and non-economic damages, and pursuing UM/UIM (uninsured / underinsured motorist) coverage where the at-fault driver\'s policy limit is inadequate.',
        bullets: [
          'No-fault PIP application and dispute resolution',
          'Serious injury threshold (Ins Law § 5102(d))',
          'UM/UIM coverage claims',
          'Comparative fault defense (CPLR § 1411)',
          'Property damage and rental car subrogation',
        ],
      },
      {
        title: 'Slip, Trip & Fall',
        description:
          'Slip and fall cases turn on notice. We build the case for actual or constructive notice of the dangerous condition — prior accident reports, prior complaints, maintenance schedules, surveillance footage, and (where the condition is recurrent) the documentary trail showing the defendant should have known. We defend against the "open and obvious" defense and the "transient condition" defense that defendants reflexively assert.',
        bullets: [
          'Actual and constructive notice proof',
          'Surveillance footage preservation',
          'Open and obvious defense rebuttal',
          'Black ice and weather-related claims',
          'Building code and ADA violation overlays',
        ],
      },
      {
        title: 'Premises Liability',
        description:
          'Beyond slip and fall, premises cases include apartment hazards, inadequate security claims (the Burgos line of cases for negligent security against landlords), swimming pool incidents, dog bites under New York\'s one-bite/known-vicious-propensity rule, and elevator and escalator malfunction claims. The notice-based negligence framework applies, and the strength of the case is usually built in the maintenance records.',
        bullets: [
          'Inadequate security claims',
          'Apartment hazards and code violations',
          'Swimming pool and recreational facility incidents',
          'Dog bites (known vicious propensity)',
          'Elevator and escalator malfunction',
        ],
      },
      {
        title: 'Medical Malpractice',
        description:
          'Medical malpractice cases require a Certificate of Merit under CPLR § 3012-a — an attorney affidavit confirming consultation with a qualified expert who has reviewed the records and concluded that there is a reasonable basis for the claim. The two-and-a-half-year statute under CPLR § 214-a, the continuous-treatment doctrine, and the foreign-object discovery rule all interact to determine when the SOL actually expires. The cases are document-heavy and expert-driven.',
        bullets: [
          'CPLR § 3012-a Certificate of Merit',
          'Continuous treatment doctrine',
          'Foreign object discovery rule',
          'Expert witness coordination',
          'Birth injury and surgical error claims',
        ],
      },
      {
        title: 'Construction Accidents (Scaffold Law)',
        description:
          'New York Labor Law §§ 200, 240(1), and 241(6) create a uniquely worker-protective scheme on construction sites. Section 240(1) — the "Scaffold Law" — imposes absolute liability on the owner and general contractor for elevation-related injuries (falls from heights, falling objects), with comparative fault NOT available as a defense. Section 241(6) imposes liability for violation of Industrial Code regulations. Section 200 codifies the common-law duty to provide a safe workplace.',
        bullets: [
          'Labor Law § 240(1) absolute liability (Scaffold Law)',
          'Labor Law § 241(6) Industrial Code violations',
          'Labor Law § 200 common-law negligence',
          'Workers\' compensation lien navigation',
          'Recalcitrant worker and sole proximate cause defenses',
        ],
      },
      {
        title: 'Wrongful Death',
        description:
          'A wrongful death action is brought by the personal representative of the decedent\'s estate under EPTL § 5-4.1 for the pecuniary loss suffered by the distributees. Pain and suffering of the decedent before death is recovered through a separate survival action. The SOL is two years from death. The proceeds are distributed to the distributees by the same intestacy framework that would have applied if the decedent had died without a will, regardless of what the will actually says.',
        bullets: [
          'EPTL § 5-4.1 pecuniary loss claim',
          'Survival action for pre-death pain and suffering',
          'Personal representative appointment in Surrogate\'s Court',
          'Distribution to statutory distributees',
          'Coordination with the estate administration',
        ],
      },
    ],
    nyFacts: {
      headline: 'New York Personal Injury — Key Facts',
      intro: 'The statutes, deadlines, and doctrines that govern injury litigation in New York.',
      facts: [
        { label: 'SOL — negligence', value: '3 years from injury (CPLR § 214)' },
        { label: 'SOL — medical malpractice', value: '2.5 years from act / end of continuous treatment (CPLR § 214-a)' },
        { label: 'SOL — wrongful death', value: '2 years from death (EPTL § 5-4.1)' },
        { label: 'SOL — municipal defendants', value: '1 year + 90 days, with Notice of Claim within 90 days (GML §§ 50-e, 50-i)' },
        { label: 'Comparative fault', value: 'Pure comparative — recovery reduced by % fault, never barred (CPLR § 1411)' },
        { label: 'No-fault PIP cap', value: '$50,000 of medical and lost wages (Ins Law § 5102)' },
        { label: 'Serious injury threshold', value: 'Required to sue auto driver beyond PIP (Ins Law § 5102(d))' },
        { label: 'Construction Scaffold Law', value: 'Absolute liability on owner / GC for elevation injuries (Labor Law § 240(1))' },
      ],
    },
    process: {
      headline: 'Our Personal Injury Process',
      intro: 'A defined path from intake through resolution — contingency fee, no recovery, no fee.',
      steps: [
        {
          title: 'Free Case Evaluation',
          description:
            'We meet with the injured party, review the police or incident report, the medical records to date, and any photographs or video, and assess the liability and damages posture. The engagement is on a contingency-fee basis — no fee is owed unless we recover.',
        },
        {
          title: 'Investigation & Evidence Preservation',
          description:
            'Evidence in injury cases disappears fast — surveillance footage is overwritten in days, witnesses move, conditions change. We send preservation letters to defendants, subpoena video, locate witnesses, photograph the scene, and retain experts (accident reconstructionists, biomechanical engineers, life-care planners) where the case warrants.',
        },
        {
          title: 'Medical Treatment Monitoring',
          description:
            'Consistent treatment is essential — both for the client\'s recovery and for the documentary record. We track the medical course, coordinate with the treating providers on the narrative reports that the case will require, and protect the client from the no-fault carrier\'s independent medical examinations (IMEs) used to cut off benefits.',
        },
        {
          title: 'Demand & Negotiation',
          description:
            'Once treatment has stabilized, we prepare a detailed demand package — liability theory, medical chronology, damages calculation (past and future medical, past and future lost wages, pain and suffering) — and open settlement negotiations with the insurance carrier. The carrier\'s opening offer is rarely the carrier\'s last offer.',
        },
        {
          title: 'Litigation & Discovery',
          description:
            'If negotiation does not produce a fair settlement, we file suit. Discovery follows — depositions of the plaintiff, the defendant, the witnesses; document demands; medical examinations under CPLR § 3121; expert disclosure under CPLR § 3101(d). The case posture shifts substantially once the defendant\'s carrier sees the case we will actually present at trial.',
        },
        {
          title: 'Trial or Settlement',
          description:
            'Most cases settle at the conclusion of discovery, on the eve of trial, or during trial itself. We try the cases that need to be tried — to verdict — and we prepare every case as if it will be tried, because the cases that settle for full value are the cases the defendant\'s carrier believes will be tried hard.',
        },
      ],
    },
    faq: [
      {
        question: 'How long do I have to sue after an accident in New York?',
        answer:
          'It depends on the type of claim and the defendant. General negligence — three years under CPLR § 214. Medical malpractice — two and a half years under CPLR § 214-a from the act or the end of continuous treatment. Wrongful death — two years from death under EPTL § 5-4.1. Claims against the City of New York, the MTA, NYC Transit, or any other municipal defendant — Notice of Claim within ninety days and lawsuit within one year and ninety days, under General Municipal Law §§ 50-e and 50-i. Missing the statute of limitations is fatal — the case is dismissed regardless of the merits.',
      },
      {
        question: 'What is no-fault insurance?',
        answer:
          'No-fault is the New York scheme codified at Insurance Law § 5102 under which your own auto insurance carrier pays the first $50,000 of medical bills, lost wages (up to $2,000 per month), and related expenses after an auto accident — regardless of whose fault the accident was. The trade-off is that to sue the at-fault driver beyond the no-fault PIP benefits, the injured party must establish a "serious injury" under § 5102(d). No-fault applies to drivers, passengers, and pedestrians struck by motor vehicles.',
      },
      {
        question: 'What is the "serious injury" threshold?',
        answer:
          'Insurance Law § 5102(d) defines serious injury as a personal injury that results in death; dismemberment; significant disfigurement; fracture; loss of a fetus; permanent loss of use of a body organ, member, function, or system; permanent consequential limitation of use of a body organ or member; significant limitation of use of a body function or system; or a medically determined injury or impairment that prevents the injured person from performing substantially all of the material acts that constitute his or her usual and customary daily activities for not less than ninety days during the one hundred eighty days immediately following the accident. The threshold is the central battleground in most NYC auto cases.',
      },
      {
        question: 'What happens if I was partly at fault for the accident?',
        answer:
          'New York is a pure comparative fault state under CPLR § 1411. Your recovery is reduced by your percentage of fault, but it is never barred. If a jury finds you 30% at fault, you recover 70% of your damages. If a jury finds you 70% at fault, you still recover 30% of your damages. Comparative fault is one of the most important reasons New York remains a viable forum for catastrophic-injury litigation — many other states bar recovery once the plaintiff\'s fault crosses 50% or 51%.',
      },
      {
        question: 'How much is my case worth?',
        answer:
          'Case value depends on the liability picture (clear liability versus comparative fault), the nature and permanence of the injuries, past and future medical expenses, past and future lost earnings, the impact on activities of daily living, and the venue and judge. Two cases with the same injury can produce very different recoveries based on liability and venue. We do not quote a number until we have the medical records, the wage records, and the liability discovery in hand — but we will tell you honestly what the case is worth once we do.',
      },
      {
        question: 'Do I have to sue, or can my case settle?',
        answer:
          'Most personal injury cases settle without trial. The questions are when and for how much. Some settle pre-suit on a demand letter and negotiation. Most settle at the end of discovery, on the eve of trial, or during trial itself. A small number go to verdict. We prepare every case as if it will be tried, because the cases that settle for full value are the ones the carrier believes we will actually try.',
      },
      {
        question: 'What is the construction Scaffold Law?',
        answer:
          'New York Labor Law § 240(1) — known as the Scaffold Law — imposes absolute liability on the owner and general contractor of a construction project for elevation-related injuries to workers (falls from heights, falling objects). Unlike ordinary negligence, comparative fault is not a defense to a Section 240(1) claim. The narrow exceptions are recalcitrant worker (the worker affirmatively refused a safety device that was readily available) and sole proximate cause (the worker\'s own action was the only cause of the injury). The statute is one of the most worker-protective in the country.',
      },
      {
        question: 'Can I sue the City of New York?',
        answer:
          'Yes — but the procedure is unforgiving. A Notice of Claim must be served on the City Comptroller within ninety days of the accident under General Municipal Law § 50-e. The lawsuit must be commenced within one year and ninety days of the accident under § 50-i. A 50-h hearing (a transcribed pre-suit examination of the claimant) is typically required before the lawsuit can proceed. The City of New York, NYC Transit, the MTA, the NYPD, the FDNY, the DOE, and the Health & Hospitals Corporation are all subject to the same regime.',
      },
      {
        question: 'Will my case go to trial?',
        answer:
          'Most cases do not. The vast majority of New York personal injury cases settle at some point — pre-suit, during discovery, at the eve of trial, or during trial. The cases that go to verdict are typically those with a fundamental liability dispute or a damages valuation gap that neither side will close. When the case must be tried, it must be tried — and we try them.',
      },
      {
        question: 'What does it cost to hire a personal injury attorney?',
        answer:
          'Nothing out of pocket. Personal injury cases are handled on a contingency fee — the firm advances costs and disbursements (medical record retrieval, expert fees, court filing fees, deposition transcripts) and is paid a percentage of the recovery only if and when there is a recovery. The standard New York contingency fee is one-third of the net recovery (after disbursements). No recovery, no fee. The engagement letter spells out the percentages, the cost-advance structure, and the lien priority in detail.',
      },
    ],
    cta: {
      headline: 'Free Personal Injury Consultation',
      body: 'Schedule a free 30-minute case evaluation. We will review the facts, the medical situation, and the liability posture — and give you an honest assessment of the case. Contingency fee: no recovery, no fee.',
    },
    related: ['family-law', 'real-estate', 'estate-planning'],
    author: {
      name: 'Russel Morgan, Esq.',
      title: 'Founder & Principal Attorney',
      credentials: 'Admitted New York · New Jersey · decades of estate practice',
    },
  },
};

/**
 * Static label registry for related-practice-area cards. Even though only
 * Probate ships in Phase 2A, the related[] list references slugs that will
 * be filled in by Phase 2B. The label lookup is used by PracticeAreaTemplate
 * to render the related cards with a human-readable title.
 */
export const practiceAreaLabels: Record<string, { label: string; blurb: string }> = {
  'estate-planning': {
    label: 'Estate Planning',
    blurb: 'Wills, trusts, healthcare directives, and the full lifetime plan.',
  },
  probate: {
    label: 'Probate',
    blurb: "Surrogate's Court representation for executors and beneficiaries.",
  },
  trusts: {
    label: 'Wills & Trusts',
    blurb: 'Revocable and irrevocable trusts, pour-over wills, funding strategy.',
  },
  'asset-protection': {
    label: 'Asset Protection',
    blurb: 'Shield wealth from creditors, lawsuits, and Medicaid spend-down.',
  },
  guardianship: {
    label: 'Guardianship',
    blurb: 'Article 81 guardianship petitions and contested proceedings.',
  },
  'power-of-attorney': {
    label: 'Power of Attorney',
    blurb: 'NY statutory short-form POAs and healthcare proxies.',
  },
  'elder-law': {
    label: 'Elder Law',
    blurb: 'Medicaid planning, long-term care, and senior asset protection.',
  },
  'family-law': {
    label: 'Family Law',
    blurb: 'Divorce, custody, child support, and equitable distribution.',
  },
  matrimonial: {
    label: 'Matrimonial',
    blurb: 'High-asset divorce, prenuptial agreements, and business valuation disputes.',
  },
  'real-estate': {
    label: 'Real Estate',
    blurb: 'Co-op and condo closings, deed transfers, commercial leases, RPL & RPAPL.',
  },
  'personal-injury': {
    label: 'Personal Injury',
    blurb: 'No-fault auto, slip & fall, premises liability, construction Labor Law § 240(1).',
  },
};

// Merge migration-rebuilt practice areas (/medicaid-planning/, /wills/,
// /wills-and-trusts/, /civil-rights/) into the registry. See practiceAreasExtra.ts.
Object.assign(practiceAreas, extraPracticeAreas);
Object.assign(practiceAreaLabels, extraPracticeAreaLabels);
